import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { 
  Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight,
  Sparkles, Users, Wrench, Clock, MapPin, User, MoreHorizontal,
  Trash2, Edit, Check, X, ExternalLink, UserPlus, Mail, Building2,
  Camera, Upload, CheckCircle2, FileText, Send, Loader2, Zap, Eye, Phone,
  Filter, List, LayoutGrid, CalendarDays, Image as ImageIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import { Checkbox } from '../components/ui/checkbox';
import DocumentLightbox from '../components/DocumentLightbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SearchSelect from '../components/SearchSelect';
import CcSelector from '../components/CcSelector';
import { appointmentAPI, customerAPI, propertyAPI, cleaningAPI, cleanerAPI, usersAPI, installationAPI, scheduledInstallationAPI } from '../lib/api';
import { formatDate, getGoogleMapsUrl, getWazeUrl, formatPhoneNumber } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { format, parse, startOfWeek, addDays, isSameDay, isToday, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, addMonths, subMonths } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

// Time slots for week view (8:00 - 18:00)
const TIME_SLOTS = Array.from({ length: 11 }, (_, i) => i + 8);

const APPOINTMENT_TYPES = {
  cleaning: { label: 'Reiniging', icon: Sparkles, color: 'purple' },
  sales: { label: 'Sales Afspraak', icon: Users, color: 'blue' },
  installation: { label: 'Installatie', icon: Wrench, color: 'amber' },
  electricity: { label: 'Elektriciteit', icon: Zap, color: 'red' },
};

// Format date as YYYY-MM-DD in local timezone (not UTC) to avoid off-by-one errors
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Check if a given YYYY-MM-DD day string falls within an appointment's date range
// (supports multi-day appointments via optional apt.end_date)
const isDayInAppointmentRange = (dayStr, apt) => {
  if (!apt || !apt.date) return false;
  const start = apt.date;
  const end = apt.end_date || apt.date;
  return dayStr >= start && dayStr <= end;
};

// Returns the position of dayStr within the appointment's range: 'single', 'start', 'middle', or 'end'
const getSpanPosition = (dayStr, apt) => {
  if (!apt || !apt.date) return 'single';
  const start = apt.date;
  const end = apt.end_date || apt.date;
  if (start === end) return 'single';
  if (dayStr === start) return 'start';
  if (dayStr === end) return 'end';
  return 'middle';
};

const AppointmentForm = ({ appointment, customers, properties, users, onSubmit, onCancel, loading, allAppointments = [], existingPhotos = [], onUploadPhoto = null, onDeletePhoto = null }) => {
  const { user } = useAuth();
  const isSalesUser = user?.role === 'sales';
  const isEditMode = !!(appointment && appointment.id);
  
  // Sales users can only create sales appointments
  const defaultType = isSalesUser ? 'sales' : 'installation';
  
  // Normalize assigned_to to arrays (older appointments stored a single user id string)
  const normalizeAssigned = (apt) => {
    if (!apt) return apt;
    const ids = Array.isArray(apt.assigned_to) ? apt.assigned_to : (apt.assigned_to ? [apt.assigned_to] : []);
    const names = Array.isArray(apt.assigned_to_names) && apt.assigned_to_names.length > 0
      ? apt.assigned_to_names
      : (apt.assigned_to_name ? apt.assigned_to_name.split(', ').filter(Boolean) : []);
    return { ...apt, assigned_to: ids, assigned_to_names: names };
  };

  const [formData, setFormData] = useState(() => {
    if (appointment) {
      const norm = normalizeAssigned(appointment);
      // Sales is altijd zelf de aanmaker en uitvoerder
      if (isSalesUser && user) {
        const fullName = `${user.first_name} ${user.last_name}`;
        if (!norm.created_by) {
          norm.created_by = user.id;
          norm.created_by_name = fullName;
        }
        if (!norm.assigned_to || norm.assigned_to.length === 0) {
          norm.assigned_to = [user.id];
          norm.assigned_to_names = [fullName];
          norm.assigned_to_name = fullName;
        }
      }
      return norm;
    }
    const fullName = user ? `${user.first_name} ${user.last_name}` : '';
    return {
      title: '',
      appointment_type: defaultType,
      date: formatLocalDate(new Date()),
      end_date: '',
      time: '09:00',
      end_time: '',
      all_day: false,
      duration_minutes: 60,
      customer_id: '',
      property_id: '',
      created_by: user?.id || '',
      created_by_name: fullName,
      assigned_to: isSalesUser && user ? [user.id] : [],
      assigned_to_names: isSalesUser && user ? [fullName] : [],
      assigned_to_name: isSalesUser && user ? fullName : '',
      description: '',
      status: 'scheduled'
    };
  });
  const [emailAction, setEmailAction] = useState('queue'); // 'queue', 'send', or 'none'
  const [ccEmails, setCcEmails] = useState([]); // CC recipients for cleaning confirmation

  // Foto's (legplan, wateraansluiting, ...) — bij create lokaal verzamelen, bij edit direct uploaden
  const [pendingPhotos, setPendingPhotos] = useState([]); // File[] (alleen create)
  const [formPhotos, setFormPhotos] = useState(existingPhotos || []); // bestaande foto's (edit)
  const [photoBusy, setPhotoBusy] = useState(false);

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    if (isEditMode && onUploadPhoto) {
      setPhotoBusy(true);
      try {
        for (const file of files) {
          const photo = await onUploadPhoto(file);
          if (photo) setFormPhotos(prev => [...prev, photo]);
        }
      } finally {
        setPhotoBusy(false);
      }
    } else {
      setPendingPhotos(prev => [...prev, ...files]);
    }
  };

  const handleRemoveExistingPhoto = async (photoId) => {
    if (!onDeletePhoto) return;
    setPhotoBusy(true);
    try {
      const ok = await onDeletePhoto(photoId);
      if (ok !== false) setFormPhotos(prev => prev.filter(p => p.id !== photoId));
    } finally {
      setPhotoBusy(false);
    }
  };

  // Check if multi-day is allowed (installation or cleaning only)
  const allowMultiDay = ['installation', 'cleaning', 'electricity', 'intern'].includes(formData.appointment_type);

  // Calculate duration in minutes from start and end time
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 60;
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    return endMinutes > startMinutes ? endMinutes - startMinutes : 60;
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // Auto-calculate duration when times change
      if (field === 'time' || field === 'end_time') {
        newData.duration_minutes = calculateDuration(
          field === 'time' ? value : prev.time,
          field === 'end_time' ? value : prev.end_time
        );
      }
      return newData;
    });
  };

  const handleUserChange = (userId) => {
    const selectedUser = users.find(u => u.id === userId);
    if (selectedUser) {
      setFormData(prev => ({
        ...prev,
        created_by: selectedUser.id,
        created_by_name: `${selectedUser.first_name} ${selectedUser.last_name}`
      }));
    }
  };

  const toggleAssignedUser = (userId) => {
    setFormData(prev => {
      const current = Array.isArray(prev.assigned_to) ? prev.assigned_to : (prev.assigned_to ? [prev.assigned_to] : []);
      const ids = current.includes(userId) ? current.filter(i => i !== userId) : [...current, userId];
      const names = ids.map(id => {
        const u = users.find(x => x.id === id);
        return u ? `${u.first_name} ${u.last_name}` : '';
      }).filter(Boolean);
      return { ...prev, assigned_to: ids, assigned_to_names: names, assigned_to_name: names.join(', ') };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...formData, emailAction, ccEmails, ...(isEditMode ? {} : { pendingPhotos }) };
    if (data.end_date && data.date && data.end_date < data.date) data.end_date = '';
    onSubmit(data);
  };

  // Filter properties by selected customer
  const filteredProperties = formData.customer_id 
    ? properties.filter(p => p.customer_id === formData.customer_id)
    : properties;

  // Helper for hover tooltip in calendar
  const getAppointmentsForDate = (date) => {
    const dateStr = formatLocalDate(date);
    return allAppointments.filter(a => a.date === dateStr);
  };

  // Helper to format duration
  const formatDuration = (minutes) => {
    if (!minutes) return '';
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}u${mins}` : `${hours}u`;
    }
    return `${minutes}min`;
  };

  // Helper to get city from appointment
  const getAppointmentCity = (appointment) => {
    if (appointment.property_id) {
      const prop = properties.find(p => p.id === appointment.property_id);
      if (prop?.city) return prop.city;
    }
    if (appointment.customer_id) {
      const cust = customers.find(c => c.id === appointment.customer_id);
      if (cust?.city) return cust.city;
    }
    return null;
  };

  // Custom day content with hover tooltip for the form calendar
  const FormDayWithTooltip = ({ date }) => {
    const dayAppointments = getAppointmentsForDate(date);
    const hasAppointments = dayAppointments.length > 0;
    
    if (!hasAppointments) {
      return <span>{date.getDate()}</span>;
    }

    const cleanings = dayAppointments.filter(a => a.appointment_type === 'cleaning');
    const sales = dayAppointments.filter(a => a.appointment_type === 'sales');
    const installations = dayAppointments.filter(a => a.appointment_type === 'installation');
    const electricity = dayAppointments.filter(a => a.appointment_type === 'electricity');
    const interns = dayAppointments.filter(a => a.appointment_type === 'intern');

    // Render appointment info with duration and city
    const renderAppointment = (a, i) => {
      const duration = formatDuration(a.duration_minutes);
      const city = getAppointmentCity(a);
      const timeStr = a.all_day ? 'Hele dag' : (a.end_time ? `${a.time}-${a.end_time}` : a.time);
      return (
        <div key={i} className="text-xs pl-4 text-muted-foreground space-y-0.5">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-medium">{timeStr}</span>
            {duration && <span className="text-muted-foreground/70">({duration})</span>}
            <span>-</span>
            <span>{a.customer_name || a.title}</span>
            {city && <span className="text-blue-400/80">• {city}</span>}
          </div>
          {a.assigned_to_name && <div className="text-primary/70">→ {a.assigned_to_name}</div>}
        </div>
      );
    };

    return (
      <HoverCard openDelay={200} closeDelay={100}>
        <HoverCardTrigger asChild>
          <span className="cursor-pointer font-bold">{date.getDate()}</span>
        </HoverCardTrigger>
        <HoverCardContent className="w-80 p-3" side="right" align="start">
          <div className="space-y-2">
            <p className="font-semibold text-sm border-b pb-1">
              {format(date, 'd MMMM yyyy', { locale: nl })}
            </p>
            <p className="text-xs text-muted-foreground">{dayAppointments.length} afspraken</p>
            
            {cleanings.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-purple-400">
                  <Sparkles className="w-3 h-3" />
                  Reinigingen ({cleanings.length})
                </div>
                {cleanings.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {cleanings.length > 3 && <div className="text-xs pl-4 text-muted-foreground">+{cleanings.length - 3} meer...</div>}
              </div>
            )}
            
            {sales.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-blue-400">
                  <Users className="w-3 h-3" />
                  Sales ({sales.length})
                </div>
                {sales.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {sales.length > 3 && <div className="text-xs pl-4 text-muted-foreground">+{sales.length - 3} meer...</div>}
              </div>
            )}
            
            {installations.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-amber-400">
                  <Wrench className="w-3 h-3" />
                  Installaties ({installations.length})
                </div>
                {installations.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {installations.length > 3 && <div className="text-xs pl-4 text-muted-foreground">+{installations.length - 3} meer...</div>}
              </div>
            )}
            
            {electricity.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-yellow-400">
                  <Zap className="w-3 h-3" />
                  Elektriciteit ({electricity.length})
                </div>
                {electricity.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {electricity.length > 3 && <div className="text-xs pl-4 text-muted-foreground">+{electricity.length - 3} meer...</div>}
              </div>
            )}
            
            {interns.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-emerald-500">
                  <Building2 className="w-3 h-3" />
                  Intern ({interns.length})
                </div>
                {interns.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {interns.length > 3 && <div className="text-xs pl-4 text-muted-foreground">+{interns.length - 3} meer...</div>}
              </div>
            )}
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="space-y-2">
        <Label>Titel *</Label>
        <Input
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="bv. Panelen reinigen, Intake gesprek..."
          required
          data-testid="appointment-title"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Type *</Label>
          <Select value={formData.appointment_type} onValueChange={(v) => handleChange('appointment_type', v)} disabled={isSalesUser}>
            <SelectTrigger data-testid="appointment-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {!isSalesUser && (
                <SelectItem value="cleaning">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    Reiniging
                  </div>
                </SelectItem>
              )}
              <SelectItem value="sales">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  Sales Afspraak
                </div>
              </SelectItem>
              {!isSalesUser && (
                <SelectItem value="installation">
                  <div className="flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-amber-400" />
                    Installatie
                  </div>
                </SelectItem>
              )}
              {!isSalesUser && (
                <SelectItem value="electricity">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    Elektriciteit
                  </div>
                </SelectItem>
              )}
              {!isSalesUser && (
                <SelectItem value="intern">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-emerald-500" />
                    Intern
                  </div>
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="scheduled">Gepland</SelectItem>
              <SelectItem value="completed">Voltooid</SelectItem>
              <SelectItem value="cancelled">Geannuleerd</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Datum rij */}
      <div className={`grid gap-4 ${allowMultiDay ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <div className="space-y-2">
          <Label>{allowMultiDay ? 'Startdatum *' : 'Datum *'}</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
                data-testid="appointment-date"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.date ? format(parse(formData.date, 'yyyy-MM-dd', new Date()), 'd MMM yyyy', { locale: nl }) : 'Selecteer'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={formData.date ? parse(formData.date, 'yyyy-MM-dd', new Date()) : undefined}
                onSelect={(date) => date && handleChange('date', format(date, 'yyyy-MM-dd'))}
                locale={nl}
                initialFocus
                components={{
                  DayContent: ({ date }) => <FormDayWithTooltip date={date} />
                }}
              />
            </PopoverContent>
          </Popover>
        </div>
        
        {/* Einddatum - alleen voor installatie en reiniging */}
        {allowMultiDay && (
          <div className="space-y-2">
            <Label>Einddatum <span className="text-muted-foreground text-xs">(optioneel)</span></Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                  data-testid="appointment-end-date"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.end_date ? format(parse(formData.end_date, 'yyyy-MM-dd', new Date()), 'd MMM yyyy', { locale: nl }) : 'Zelfde dag'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.end_date ? parse(formData.end_date, 'yyyy-MM-dd', new Date()) : undefined}
                  onSelect={(date) => date && handleChange('end_date', format(date, 'yyyy-MM-dd'))}
                  locale={nl}
                  disabled={(date) => formData.date && date < parse(formData.date, 'yyyy-MM-dd', new Date())}
                  initialFocus
                  components={{
                    DayContent: ({ date }) => <FormDayWithTooltip date={date} />
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {/* Tijd rij */}
      <label className="flex items-center gap-2 cursor-pointer w-fit">
        <Checkbox
          checked={!!formData.all_day}
          onCheckedChange={(v) => setFormData(prev => ({
            ...prev, all_day: !!v,
            ...(v ? { time: '', end_time: '' } : { time: '09:00', end_time: '' })
          }))}
          data-testid="appointment-all-day"
        />
        <span className="text-sm font-medium">Hele dag</span>
      </label>
      {!formData.all_day && (
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Starttijd *</Label>
          <Input
            type="time"
            value={formData.time}
            onChange={(e) => handleChange('time', e.target.value)}
            required
            data-testid="appointment-time"
          />
        </div>
        <div className="space-y-2">
          <Label>Eindtijd <span className="text-muted-foreground text-xs">(optioneel)</span></Label>
          <Input
            type="time"
            value={formData.end_time}
            onChange={(e) => handleChange('end_time', e.target.value)}
            data-testid="appointment-end-time"
          />
          {formData.time && formData.end_time && (
            <p className="text-xs text-muted-foreground">
              Duur: {Math.floor(calculateDuration(formData.time, formData.end_time) / 60)}u{calculateDuration(formData.time, formData.end_time) % 60 > 0 ? calculateDuration(formData.time, formData.end_time) % 60 + 'min' : ''}
            </p>
          )}
        </div>
      </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Klant</Label>
          <SearchSelect
            options={customers.map(c => ({
              value: c.id,
              label: `${c.first_name} ${c.last_name}`
            }))}
            value={formData.customer_id}
            onChange={(v) => handleChange('customer_id', v)}
            placeholder="Zoek klant..."
            displayKey="label"
            valueKey="value"
          />
        </div>
        <div className="space-y-2">
          <Label>Adres</Label>
          <SearchSelect
            options={filteredProperties.map(p => ({
              value: p.id,
              label: `${p.street} ${p.number}, ${p.city}`
            }))}
            value={formData.property_id}
            onChange={(v) => handleChange('property_id', v)}
            placeholder="Zoek adres..."
            displayKey="label"
            valueKey="value"
          />
        </div>
      </div>

      {isSalesUser ? (
        <div className="space-y-1 p-3 rounded-md border bg-muted/40" data-testid="sales-auto-assignee">
          <p className="text-sm">
            <span className="text-muted-foreground">Aangemaakt door & uitvoerder: </span>
            <span className="font-medium">{formData.created_by_name || (user ? `${user.first_name} ${user.last_name}` : '')}</span>
          </p>
          <p className="text-xs text-muted-foreground">Als sales ben je automatisch de aanmaker en uitvoerder van je afspraken</p>
        </div>
      ) : (
      <>
      <div className="space-y-2">
        <Label>Aangemaakt door *</Label>
        <SearchSelect
          options={users.map(u => ({
            value: u.id,
            label: `${u.first_name} ${u.last_name}`,
            email: u.email
          }))}
          value={formData.created_by}
          onChange={handleUserChange}
          placeholder="Selecteer gebruiker..."
          displayKey="label"
          valueKey="value"
          renderOption={(option) => (
            <div className="flex flex-col">
              <span>{option.label}</span>
              <span className="text-xs text-muted-foreground">{option.email}</span>
            </div>
          )}
        />
      </div>

      <div className="space-y-2">
        <Label>Uitvoerder(s)</Label>
        <div className="border rounded-md max-h-40 overflow-y-auto p-2 space-y-0.5" data-testid="assignee-multiselect">
          {users.map(u => (
            <label key={u.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer" data-testid={`assignee-option-${u.id}`}>
              <Checkbox
                checked={(Array.isArray(formData.assigned_to) ? formData.assigned_to : [formData.assigned_to]).includes(u.id)}
                onCheckedChange={() => toggleAssignedUser(u.id)}
              />
              <span className="text-sm flex-1">{u.first_name} {u.last_name}</span>
              <span className="text-xs text-muted-foreground">{u.role === 'cleaner' ? 'Install/Clean' : u.role}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Wie voert de afspraak uit? Meerdere personen mogelijk.</p>
      </div>
      </>
      )}
      <div className="space-y-2">
        <Label>Beschrijving</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={2}
          placeholder="Extra notities..."
        />
      </div>

      {/* Foto's & documenten (legplan, wateraansluiting, ...) */}
      {!isSalesUser && (
        <div className="space-y-2 pt-2 border-t" data-testid="appointment-form-photos">
          <Label className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            Foto's & documenten
          </Label>
          <p className="text-xs text-muted-foreground">Bv. legplan (installatie) of aansluiting water (reiniging) — zichtbaar voor de uitvoerder(s)</p>
          
          {(formPhotos.length > 0 || pendingPhotos.length > 0) && (
            <div className="grid grid-cols-3 gap-2">
              {formPhotos.map((p) => (
                <div key={p.id} className="relative group">
                  {/\.pdf$/i.test(p.filename || p.url || '') ? (
                    <a href={p.url?.startsWith('http') ? p.url : `${API_URL}${p.url}`} target="_blank" rel="noopener noreferrer" className="w-full h-20 rounded border bg-muted flex flex-col items-center justify-center gap-1">
                      <FileText className="w-6 h-6 text-red-500" />
                      <span className="text-[10px] truncate max-w-full px-1">{p.filename}</span>
                    </a>
                  ) : (
                    <a href={p.url?.startsWith('http') ? p.url : `${API_URL}${p.url}`} target="_blank" rel="noopener noreferrer">
                      <img src={p.url?.startsWith('http') ? p.url : `${API_URL}${p.url}`} alt={p.filename || 'Foto'} className="w-full h-20 object-cover rounded border" loading="lazy" />
                    </a>
                  )}
                  {onDeletePhoto && (
                    <button
                      type="button"
                      onClick={() => handleRemoveExistingPhoto(p.id)}
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Verwijderen"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              {pendingPhotos.map((file, idx) => (
                <div key={`pending-${idx}`} className="relative group">
                  {file.type === 'application/pdf' ? (
                    <div className="w-full h-20 rounded border bg-muted flex flex-col items-center justify-center gap-1">
                      <FileText className="w-6 h-6 text-red-500" />
                      <span className="text-[10px] truncate max-w-full px-1">{file.name}</span>
                    </div>
                  ) : (
                    <img src={URL.createObjectURL(file)} alt={file.name} className="w-full h-20 object-cover rounded border" />
                  )}
                  <button
                    type="button"
                    onClick={() => setPendingPhotos(prev => prev.filter((_, i) => i !== idx))}
                    className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Verwijderen"
                  >
                    ×
                  </button>
                  <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] text-center py-0.5 rounded-b">nog uploaden</span>
                </div>
              ))}
            </div>
          )}
          
          <label className={`inline-flex items-center gap-2 text-sm border rounded-md px-3 py-1.5 cursor-pointer hover:bg-muted transition-colors ${photoBusy ? 'opacity-50 pointer-events-none' : ''}`}>
            {photoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {photoBusy ? 'Uploaden...' : 'Foto / document toevoegen'}
            <input
              type="file"
              accept="image/*,application/pdf"
              multiple
              className="hidden"
              onChange={handlePhotoSelect}
              data-testid="appointment-photo-input"
            />
          </label>
        </div>
      )}

      {/* Email Options - Only show for cleaning type */}
      {formData.appointment_type === 'cleaning' && (
        <div className="space-y-2 pt-2 border-t">
          <Label className="text-sm font-medium">Bevestigingsmail</Label>
          <div className="space-y-2">
            <div 
              className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${emailAction === 'queue' ? 'bg-blue-500/10 border-blue-500' : 'hover:bg-muted/50'}`}
              onClick={() => setEmailAction('queue')}
            >
              <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${emailAction === 'queue' ? 'border-blue-500' : 'border-muted-foreground'}`}>
                {emailAction === 'queue' && <div className="w-2 h-2 rounded-full bg-blue-500" />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500" />
                  Mail klaarzetten
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Mail komt in de wachtrij
                </p>
              </div>
            </div>
            
            <div 
              className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${emailAction === 'send' ? 'bg-green-500/10 border-green-500' : 'hover:bg-muted/50'}`}
              onClick={() => setEmailAction('send')}
            >
              <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${emailAction === 'send' ? 'border-green-500' : 'border-muted-foreground'}`}>
                {emailAction === 'send' && <div className="w-2 h-2 rounded-full bg-green-500" />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Send className="w-4 h-4 text-green-500" />
                  Mail direct verzenden
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Mail wordt meteen verstuurd
                </p>
              </div>
            </div>
            
            <div 
              className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${emailAction === 'none' ? 'bg-muted/50 border-muted-foreground' : 'hover:bg-muted/50'}`}
              onClick={() => setEmailAction('none')}
            >
              <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${emailAction === 'none' ? 'border-muted-foreground' : 'border-muted-foreground'}`}>
                {emailAction === 'none' && <div className="w-2 h-2 rounded-full bg-muted-foreground" />}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Geen mail versturen
                </p>
              </div>
            </div>
          </div>
          
          {/* CC Selector - only show when email will be sent */}
          {emailAction !== 'none' && (
            <CcSelector
              selectedEmails={ccEmails}
              onChange={setCcEmails}
              defaultEnabled={true}
            />
          )}
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Annuleren</Button>
        <Button type="submit" disabled={loading} data-testid="appointment-submit">
          {loading ? 'Opslaan...' : (appointment ? 'Bijwerken' : 'Aanmaken')}
        </Button>
      </DialogFooter>
    </form>
  );
};

const AppointmentCard = ({ appointment, customers, properties, onEdit, onDelete, onStatusChange, onSyncToGoogle, onSyncCleaningToGoogle, onEditCleaning, onSendCleaningMail, onEditInstallation }) => {
  const type = APPOINTMENT_TYPES[appointment.appointment_type] || APPOINTMENT_TYPES.installation;
  const Icon = type.icon;
  const customer = customers.find(c => c.id === appointment.customer_id);
  const property = properties.find(p => p.id === appointment.property_id);
  const isReadOnly = appointment.read_only || appointment.is_google_event;
  const isCleaning = appointment.is_cleaning;
  const isInstallation = appointment.is_installation;

  const statusColors = {
    scheduled: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    completed: 'bg-green-500/20 text-green-400 border-green-500/30',
    cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  const statusLabels = {
    scheduled: 'Gepland',
    completed: 'Voltooid',
    cancelled: 'Geannuleerd',
  };

  // Color mapping including green for google_cleaning
  const colorMap = {
    purple: { border: 'border-l-purple-500', bg: 'bg-purple-500/20', text: 'text-purple-400' },
    blue: { border: 'border-l-blue-500', bg: 'bg-blue-500/20', text: 'text-blue-400' },
    amber: { border: 'border-l-amber-500', bg: 'bg-amber-500/20', text: 'text-amber-400' },
    green: { border: 'border-l-green-500', bg: 'bg-green-500/20', text: 'text-green-400' },
    red: { border: 'border-l-red-500', bg: 'bg-red-500/20', text: 'text-red-400' },
  };

  const colors = colorMap[type.color] || colorMap.amber;

  // Handle card click for edit
  const handleCardClick = () => {
    if (isReadOnly) return;
    if (isCleaning && onEditCleaning) {
      onEditCleaning(appointment);
    } else if (isInstallation && onEditInstallation) {
      onEditInstallation(appointment);
    } else if (onEdit) {
      onEdit(appointment);
    }
  };

  return (
    <Card 
      className={`border-l-4 ${colors.border} ${!isReadOnly ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={handleCardClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-md flex items-center justify-center ${colors.bg}`}>
              <Icon className={`w-5 h-5 ${colors.text}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium">{appointment.title}</p>
                {isReadOnly && (
                  <Badge variant="outline" className="text-xs bg-green-500/10 text-green-400 border-green-500/30">
                    Google
                  </Badge>
                )}
                {isCleaning && (
                  <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/30">
                    CRM Reiniging
                  </Badge>
                )}
              </div>
              <div className="flex flex-col gap-1 text-xs text-muted-foreground mt-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {appointment.all_day ? 'Hele dag' : `${appointment.time || '00:00'}${appointment.end_time ? ` - ${appointment.end_time}` : ''}`}
                    {/* Toon duur in minuten alleen voor reinigingen */}
                    {!appointment.all_day && appointment.appointment_type === 'cleaning' && ` (${appointment.duration_minutes || 60} min)`}
                  </span>
                  {/* Toon adres direct naast tijd */}
                  {property && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {property.street} {property.number}, {property.city}
                    </span>
                  )}
                  {(appointment.assigned_to_name || appointment.assigned_to) && (
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      Uitvoerder: {appointment.assigned_to_name || appointment.assigned_to}
                    </span>
                  )}
                </div>
                {appointment.end_date && appointment.end_date !== appointment.date && (
                  <span className="flex items-center gap-1 text-primary">
                    <CalendarIcon className="w-3 h-3" />
                    {format(parse(appointment.date, 'yyyy-MM-dd', new Date()), 'd MMM', { locale: nl })} - {format(parse(appointment.end_date, 'yyyy-MM-dd', new Date()), 'd MMM', { locale: nl })}
                  </span>
                )}
              </div>
              {/* Created by info */}
              {appointment.created_by_name && (
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <UserPlus className="w-3 h-3" />
                  Aanmaker: <span className="text-foreground">{appointment.created_by_name}</span>
                </p>
              )}
              {customer && (
                <Link 
                  to={`/customers/${customer.id}`}
                  className="text-xs text-primary hover:underline mt-1 block"
                  onClick={(e) => e.stopPropagation()}
                >
                  {customer.first_name} {customer.last_name}
                </Link>
              )}
              {property && (
                <a 
                  href={getGoogleMapsUrl(property)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 mt-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MapPin className="w-3 h-3" />
                  {property.street} {property.number}, {property.city}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {/* Show location for Google Calendar events */}
              {isReadOnly && appointment.location && (
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(appointment.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 mt-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MapPin className="w-3 h-3" />
                  {appointment.location}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={statusColors[appointment.status]}>
              {statusLabels[appointment.status]}
            </Badge>
            {/* Show actions for non-read-only appointments OR cleanings (which can be deleted) */}
            {(!isReadOnly || isCleaning) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`appointment-menu-${appointment.id}`}>
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {!isCleaning && appointment.status === 'scheduled' && (
                    <DropdownMenuItem onClick={() => onSyncToGoogle(appointment)}>
                      <CalendarIcon className="w-4 h-4 mr-2 text-primary" />
                      Naar Google Calendar
                    </DropdownMenuItem>
                  )}
                  {isCleaning && (
                    <DropdownMenuItem onClick={() => onSyncCleaningToGoogle(appointment, customer, property)}>
                      <CalendarIcon className="w-4 h-4 mr-2 text-primary" />
                      Naar Google Calendar
                    </DropdownMenuItem>
                  )}
                  {isCleaning && onEditCleaning && (
                    <DropdownMenuItem onClick={() => onEditCleaning(appointment)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Bewerken
                    </DropdownMenuItem>
                  )}
                  {isCleaning && onSendCleaningMail && (
                    <DropdownMenuItem onClick={() => onSendCleaningMail(appointment, customer, property)}>
                      <Mail className="w-4 h-4 mr-2 text-blue-500" />
                      Mail maken
                    </DropdownMenuItem>
                  )}
                  {!isCleaning && appointment.status === 'scheduled' && (
                    <DropdownMenuItem onClick={() => onStatusChange(appointment.id, 'completed')}>
                      <Check className="w-4 h-4 mr-2 text-green-400" />
                      Markeer als voltooid
                    </DropdownMenuItem>
                  )}
                  {!isCleaning && appointment.status === 'scheduled' && (
                    <DropdownMenuItem onClick={() => onStatusChange(appointment.id, 'cancelled')}>
                      <X className="w-4 h-4 mr-2 text-red-400" />
                      Annuleren
                    </DropdownMenuItem>
                  )}
                  {!isCleaning && !isInstallation && (
                    <DropdownMenuItem onClick={() => onEdit(appointment)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Bewerken
                    </DropdownMenuItem>
                  )}
                  {isInstallation && (
                    <DropdownMenuItem onClick={() => onEditInstallation && onEditInstallation(appointment)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Bewerken
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem 
                    onClick={() => onDelete(appointment.id)} 
                    className="text-destructive"
                    data-testid={`delete-appointment-${appointment.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Verwijderen
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Simplified appointment card for cleaners
const CleanerAppointmentCard = ({ appointment, onMarkComplete, onMarkCancelled, onReschedule, onUploadPhoto, t }) => {
  const statusColors = {
    scheduled: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    completed: 'bg-green-500/20 text-green-400 border-green-500/30',
    cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  const statusLabels = {
    scheduled: t ? t('pending') : 'Gepland',
    completed: t ? t('completed') : 'Voltooid',
    cancelled: t ? t('cancelled') : 'Niet voltooid',
  };

  const property = appointment.property;
  const isCompleted = appointment.status === 'completed';
  const isCancelled = appointment.status === 'cancelled';
  const isScheduled = appointment.status === 'scheduled' || !appointment.status;

  // Determine border color based on status
  const borderColor = isCompleted ? 'border-l-green-500' : isCancelled ? 'border-l-red-500' : 'border-l-purple-500';
  const iconBg = isCompleted ? 'bg-green-500/20' : isCancelled ? 'bg-red-500/20' : 'bg-purple-500/20';

  // Format the date nicely based on language
  const formatCleaningDate = (dateStr) => {
    if (!dateStr) return t ? t('date') : 'Geen datum';
    try {
      const date = new Date(dateStr);
      // Get language from localStorage for date formatting
      const lang = localStorage.getItem('language') || 'nl';
      const locales = { nl: 'nl-BE', en: 'en-GB', pl: 'pl-PL', uk: 'uk-UA' };
      const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
      return date.toLocaleDateString(locales[lang] || 'nl-BE', options);
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className={`border-l-4 ${borderColor}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-md flex items-center justify-center ${iconBg}`}>
              {isCompleted ? (
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              ) : isCancelled ? (
                <X className="w-5 h-5 text-red-400" />
              ) : (
                <Sparkles className="w-5 h-5 text-purple-400" />
              )}
            </div>
            <div>
              {/* Date prominently displayed */}
              <div className="flex items-center gap-2 mb-1">
                <CalendarIcon className="w-4 h-4 text-orange-400" />
                <span className="font-semibold text-orange-400">
                  {formatCleaningDate(appointment.date)}
                </span>
              </div>
              <p className="font-medium">
                {appointment.customer_name || 'Onbekend'}
              </p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {appointment.time || '09:00'}
                </span>
                {appointment.panel_count > 0 && (
                  <span className="flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    {appointment.panel_count} {t ? t('panels') : 'panelen'}
                  </span>
                )}
              </div>
              {/* Show cancel reason if cancelled */}
              {isCancelled && (appointment.original_cleaning?.cancel_reason || appointment.cancel_reason) && (
                <p className="text-xs text-red-400 mt-1">
                  Reden: {appointment.original_cleaning?.cancel_reason || appointment.cancel_reason}
                </p>
              )}
              {property && (
                <a 
                  href={getGoogleMapsUrl(property)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 mt-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MapPin className="w-3 h-3" />
                  {property.street} {property.number}, {property.postal_code} {property.city}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {/* Show notes/opmerkingen if available */}
              {(appointment.original_cleaning?.notes || appointment.description) && (
                <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-md">
                  <p className="text-xs font-semibold text-yellow-400 flex items-center gap-1 mb-1">
                    <FileText className="w-3 h-3" />
                    {t ? t('notes') : 'Opmerkingen'}:
                  </p>
                  <p className="text-sm text-yellow-200 whitespace-pre-wrap">
                    {appointment.original_cleaning?.notes || appointment.description}
                  </p>
                </div>
              )}
              {/* Show instruction photo if available */}
              {appointment.original_cleaning?.instruction_photo_url && (
                <div className="mt-2">
                  <p className="text-xs font-semibold text-blue-400 flex items-center gap-1 mb-1">
                    <Camera className="w-3 h-3" />
                    Instructiefoto:
                  </p>
                  <a 
                    href={appointment.original_cleaning.instruction_photo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <img 
                      src={appointment.original_cleaning.instruction_photo_url} 
                      alt="Instructiefoto"
                      className="max-w-full h-auto max-h-32 rounded-md border border-blue-500/30 hover:border-blue-500 transition-colors"
                    />
                  </a>
                </div>
              )}
              {/* Show photo attachments (legplan, wateraansluiting, ...) */}
              {appointment.photos?.length > 0 && (
                <div className="mt-2" data-testid="cleaner-card-photos">
                  <p className="text-xs font-semibold text-blue-400 flex items-center gap-1 mb-1">
                    <Camera className="w-3 h-3" />
                    Foto's ({appointment.photos.length}):
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {appointment.photos.map(p => (
                      <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={p.url}
                          alt={p.description || p.filename || 'Foto'}
                          className="h-20 w-20 object-cover rounded-md border border-blue-500/30 hover:border-blue-500 transition-colors"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={statusColors[appointment.status] || statusColors.scheduled}>
              {statusLabels[appointment.status] || 'Gepland'}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isScheduled && (
                  <>
                    <DropdownMenuItem 
                      onClick={() => onMarkComplete(appointment.original_id || appointment.id.replace('cleaning-', ''))}
                      className="text-green-500"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {t ? t('markAsCompleted') : 'Markeer als voltooid'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => onMarkCancelled(appointment.original_id || appointment.id.replace('cleaning-', ''))}
                      className="text-red-500"
                    >
                      <X className="w-4 h-4 mr-2" />
                      {t ? t('cancel') : 'Annuleren'}
                    </DropdownMenuItem>
                  </>
                )}
                {property && (
                  <DropdownMenuItem 
                    onClick={() => onUploadPhoto(property.id, `${property.street} ${property.number}`, appointment.id)}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {t ? t('addPhoto') : 'Foto toevoegen'}
                  </DropdownMenuItem>
                )}
                {property && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground">{t ? t('navigate') : 'Navigeren naar'}</DropdownMenuLabel>
                    <DropdownMenuItem asChild>
                      <a 
                        href={getGoogleMapsUrl(property)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center cursor-pointer"
                      >
                        <MapPin className="w-4 h-4 mr-2" />
                        Google Maps
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a 
                        href={getWazeUrl(property)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center cursor-pointer"
                      >
                        <MapPin className="w-4 h-4 mr-2" />
                        Waze
                      </a>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Agenda = () => {
  const [appointments, setAppointments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newAppointmentDefaults, setNewAppointmentDefaults] = useState(null);
  const [editAppointment, setEditAppointment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [calendarData, setCalendarData] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  // Type filters (Google Calendar style)
  const [typeFilters, setTypeFilters] = useState({
    cleaning: true,
    sales: true,
    installation: true,
    electricity: true,
    intern: true
  });
  // View mode: 'calendar' (month), 'week' (Google Calendar style), or 'agenda' (mobile list)
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return 'agenda';
    return 'calendar';
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  // Calendar popup state for cleaning confirmation emails
  const [calendarPopupOpen, setCalendarPopupOpen] = useState(false);
  const [selectedCleaningForCalendar, setSelectedCleaningForCalendar] = useState(null);
  const [selectedCleaningCustomer, setSelectedCleaningCustomer] = useState(null);
  const [selectedCleaningProperty, setSelectedCleaningProperty] = useState(null);
  const [emailAction, setEmailAction] = useState('queue'); // 'queue', 'send', or 'none'
  const [sendingEmail, setSendingEmail] = useState(false);
  const [openGoogleCalendarAfter, setOpenGoogleCalendarAfter] = useState(false); // Whether to open Google Calendar after
  // Photo upload state for cleaners
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  const [photoUploadPropertyId, setPhotoUploadPropertyId] = useState(null);
  const [photoUploadCleaningId, setPhotoUploadCleaningId] = useState(null);
  const [photoUploadPropertyName, setPhotoUploadPropertyName] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoDescription, setPhotoDescription] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [lightboxDoc, setLightboxDoc] = useState(null); // Popup viewer voor foto/document
  // Cancel cleaning dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cleaningToCancel, setCleaningToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  // Edit cleaning dialog state
  const [editCleaningOpen, setEditCleaningOpen] = useState(false);
  const [cleaningToEditData, setCleaningToEditData] = useState(null);
  const [editCleaningData, setEditCleaningData] = useState({
    cleaning_date: '',
    cleaning_time: '09:00',
    notes: '',
    send_confirmation: false
  });
  const [editingCleaning, setEditingCleaning] = useState(false);
  
  // Edit installation dialog state
  const [editInstallationOpen, setEditInstallationOpen] = useState(false);
  const [installationToEditData, setInstallationToEditData] = useState(null);
  const [editInstallationData, setEditInstallationData] = useState({
    installation_date: '',
    installation_end_date: '',
    installation_time: '09:00',
    installation_end_time: '17:00',
    notes: '',
    send_confirmation: false
  });
  const [editingInstallation, setEditingInstallation] = useState(false);
  
  // Drag and drop state
  const [draggedAppointment, setDraggedAppointment] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);
  
  // Day click menu state
  const [dayMenuOpen, setDayMenuOpen] = useState(false);
  const [dayMenuDate, setDayMenuDate] = useState(null);
  const [dayDetailOpen, setDayDetailOpen] = useState(false);
  const [dayDetailDate, setDayDetailDate] = useState(null);
  
  // Appointment click menu state
  const [appointmentMenuOpen, setAppointmentMenuOpen] = useState(false);
  const [appointmentMenuData, setAppointmentMenuData] = useState(null);
  const [appointmentDetailOpen, setAppointmentDetailOpen] = useState(false);
  const [appointmentDetailData, setAppointmentDetailData] = useState(null);
  const [offerteDocsForDetail, setOfferteDocsForDetail] = useState([]);

  // Legplannen/bijlagen van de gekoppelde installatie-offerte automatisch tonen in het afspraakdetail
  useEffect(() => {
    const oid = appointmentDetailData?.offerte_id;
    if (appointmentDetailData?.is_scheduled_installation && oid) {
      fetch(`${API_URL}/api/installation-offertes/${oid}/project-documents`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => (r.ok ? r.json() : []))
        .then(d => setOfferteDocsForDetail(Array.isArray(d) ? d : []))
        .catch(() => setOfferteDocsForDetail([]));
    } else {
      setOfferteDocsForDetail([]);
    }
  }, [appointmentDetailData?.offerte_id, appointmentDetailData?.is_scheduled_installation]);
  
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, hasRole } = useAuth();
  const { t } = useLanguage();
  const isCleaner = user?.role === 'cleaner';
  const isSales = user?.role === 'sales';
  const isAdmin = user?.role === 'admin';

  // Handle URL parameters for auto-opening forms (after data is loaded)
  useEffect(() => {
    if (loading || customers.length === 0) return; // Wait for data to load
    
    const action = searchParams.get('action');
    const type = searchParams.get('type');
    const customerId = searchParams.get('customer_id');
    
    if (action === 'new' && type === 'installation' && customerId) {
      // Find the customer
      const customer = customers.find(c => c.id === customerId);
      
      // Get customer's properties
      const customerProperties = properties.filter(p => p.customer_id === customerId);
      
      // Set default values for the new appointment form
      setNewAppointmentDefaults({
        appointment_type: 'installation',
        customer_id: customerId,
        property_id: customerProperties.length === 1 ? customerProperties[0].id : '',
        title: customer ? `Installatie - ${customer.first_name} ${customer.last_name}` : 'Installatie',
        date: new Date().toISOString().split('T')[0],
        time: '09:00',
        status: 'scheduled',
        duration_minutes: 240,
        description: ''
      });
      setDialogOpen(true);
      // Clear the URL parameters
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, loading, customers, properties]);

  const handleSyncToGoogle = (appointment) => {
    // Use the same URL generator function
    const url = generateGoogleCalendarUrl(appointment);
    window.open(url, '_blank');
    toast.success('Google Calendar geopend - klik op Opslaan!');
  };

  const fetchData = async () => {
    try {
      // Cleaners use limited API - but also need scheduled installations
      if (isCleaner) {
        // Fetch both cleaning appointments AND scheduled installations assigned to this user
        const [cleanerAptsRes, scheduledInstallsRes] = await Promise.all([
          cleanerAPI.getAppointments(),
          scheduledInstallationAPI.getAll().catch(() => ({ data: [] }))
        ]);
        
        // Convert cleaner appointments to standard format
        const cleanerAppointments = cleanerAptsRes.data.map(c => {
          let cleaningDate = c.cleaning_date;
          let cleaningTime = '';
          if (cleaningDate && cleaningDate.includes('T')) {
            const parts = cleaningDate.split('T');
            cleaningDate = parts[0];
            cleaningTime = parts[1]?.substring(0, 5) || '';
          }
          
          return {
            id: `cleaning-${c.id}`,
            original_id: c.id,
            title: `Reiniging ${c.property?.street || ''} ${c.property?.number || ''}`,
            appointment_type: 'cleaning',
            date: cleaningDate,
            time: cleaningTime,
            property_id: c.property?.id || '',
            property: c.property,
            customer_name: c.customer_name,
            panel_count: c.panel_count,
            assigned_to: c.performed_by || '',
            created_by_name: c.created_by_name || '',
            description: c.notes || '',
            status: c.status || 'scheduled',
            is_cleaning: true,
            photos: c.photos || [],
            original_cleaning: c
          };
        });
        
        // Filter scheduled installations assigned to this user
        const myScheduledInstallations = (scheduledInstallsRes.data || [])
          .filter(i => {
            // Check if current user is in assigned_to array
            const assignedTo = i.assigned_to || [];
            return i.installation_date && assignedTo.includes(user?.id);
          })
          .map(i => {
            const typeLabels = {
              'zonnepanelen': 'Zonnepanelen',
              'batterij': 'Batterij',
              'laadpaal': 'Laadpaal',
              'omvormer': 'Aansluiting omvormer',
              'warmtepomp': 'Warmtepomp',
              'airco': 'Airco',
              'combi': 'Combi'
            };
            const typeLabel = typeLabels[i.installation_type] || i.installation_type || 'Installatie';
            
            return {
              id: `scheduled-installation-${i.id}`,
              original_id: i.id,
              title: `${typeLabel}: ${i.customer_name || 'Klant'}`,
              appointment_type: 'installation',
              date: i.installation_date,
              end_date: i.installation_end_date,
              time: i.installation_time || '09:00',
              end_time: i.installation_end_time || '17:00',
              property_id: i.property_id,
              property_street: i.property_street,
              property_city: i.property_city,
              customer_id: i.customer_id || '',
              customer_name: i.customer_name || 'Onbekende klant',
              assigned_to: i.assigned_to || [],
              assigned_to_name: (i.assigned_to_names || []).join(', '),
              created_by_name: i.created_by || '',
              description: i.notes || '',
              status: i.status || 'gepland',
              is_installation: true,
              is_scheduled_installation: true,
              offerte_id: i.offerte_id,
              products: i.products || [],
              photos: i.photos || [],
              notes: i.notes || ''
            };
          });
        
        // Combine cleanings and installations
        const allAppointments = [...cleanerAppointments, ...myScheduledInstallations];
        setAppointments(allAppointments);
        
        // Build calendar data
        const byDate = {};
        allAppointments.forEach(apt => {
          if (!byDate[apt.date]) byDate[apt.date] = [];
          byDate[apt.date].push(apt);
        });
        setCalendarData(byDate);
        setLoading(false);
        return;
      }

      // Regular users get full data
      if (isCleaner) {
        // Cleaner logic already handled above
        return;
      }
      
      // Sales users only see their own sales appointments - no cleanings or installations
      if (isSales) {
        const [aptsRes, custsRes, propsRes] = await Promise.all([
          appointmentAPI.getAll(), // Backend filters for sales: only own sales appointments
          customerAPI.getAll().catch(() => ({ data: [] })),
          propertyAPI.getAll().catch(() => ({ data: [] }))
        ]);
        
        setAppointments(aptsRes.data);
        setCustomers(custsRes.data);
        setProperties(propsRes.data);
        setUsers([]);
        setInstallations([]);
        
        // Build calendar data
        const byDate = {};
        aptsRes.data.forEach(apt => {
          if (!byDate[apt.date]) byDate[apt.date] = [];
          byDate[apt.date].push(apt);
        });
        setCalendarData(byDate);
        setLoading(false);
        return;
      }
      
      // Admin/Technician get full data
      const [aptsRes, custsRes, propsRes, cleaningsRes, usersRes, installsRes, scheduledInstallsRes] = await Promise.all([
        appointmentAPI.getAll(),
        customerAPI.getAll(),
        propertyAPI.getAll(),
        cleaningAPI.getAll(),
        usersAPI.getAll().catch(() => ({ data: [] })),
        installationAPI.getAll().catch(() => ({ data: [] })),
        scheduledInstallationAPI.getAll().catch(() => ({ data: [] }))
      ]);
      
      // Convert cleanings to appointment-like format for unified display
      const cleaningsAsAppointments = cleaningsRes.data.map(c => {
        const prop = propsRes.data.find(p => p.id === c.property_id);
        const cust = prop ? custsRes.data.find(cu => cu.id === prop.customer_id) : null;
        
        // Extract date and time from cleaning_date
        let cleaningDate = c.cleaning_date;
        let cleaningTime = '';
        if (cleaningDate && cleaningDate.includes('T')) {
          const parts = cleaningDate.split('T');
          cleaningDate = parts[0];
          cleaningTime = parts[1]?.substring(0, 5) || '';
        }
        
        return {
          id: `cleaning-${c.id}`,
          original_id: c.id,
          title: `Reiniging ${prop ? prop.street + ' ' + prop.number : ''}`,
          appointment_type: 'cleaning',
          date: cleaningDate,
          time: cleaningTime,
          property_id: c.property_id,
          customer_id: prop?.customer_id || '',
          assigned_to: c.performed_by || '',
          assigned_to_name: (c.assigned_to_names || []).join(', ') || c.performed_by || '',
          created_by_name: c.created_by_name || '',
          description: c.notes || '',
          status: 'scheduled',
          is_cleaning: true,
          photos: c.photos || [],
          original_cleaning: c
        };
      });
      
      // Convert SCHEDULED installations to appointment-like format (from In te plannen feature)
      const scheduledInstallationsAsAppointments = (scheduledInstallsRes.data || [])
        .filter(i => i.installation_date) // Must have a date
        .map(i => {
          // Find customer if customer_id exists
          const cust = i.customer_id ? custsRes.data.find(cu => cu.id === i.customer_id) : null;
          const customerName = cust ? `${cust.first_name || ''} ${cust.last_name || ''}`.trim() : 'Onbekende klant';
          
          // Get installation type label
          const typeLabels = {
            'zonnepanelen': 'Zonnepanelen',
            'batterij': 'Batterij',
            'laadpaal': 'Laadpaal',
            'omvormer': 'Aansluiting omvormer',
            'warmtepomp': 'Warmtepomp',
            'airco': 'Airco',
            'combi': 'Combi'
          };
          const typeLabel = typeLabels[i.installation_type] || i.installation_type || 'Installatie';
          const address = i.property_street ? `${i.property_street}, ${i.property_city || ''}`.trim() : '';
          
          return {
            id: `scheduled-installation-${i.id}`,
            original_id: i.id,
            title: `${typeLabel}: ${customerName}`,
            appointment_type: 'installation',
            date: i.installation_date,
            end_date: i.installation_end_date,
            time: i.installation_time || '09:00',
            end_time: i.installation_end_time || '17:00',
            property_id: i.property_id,
            property_street: i.property_street,
            property_city: i.property_city,
            customer_id: i.customer_id || '',
            customer_name: customerName,
            assigned_to: i.assigned_to || [],
            assigned_to_name: (i.assigned_to_names || []).join(', '),
            created_by_name: i.created_by || '',
            description: i.notes || '',
            status: i.status || 'gepland',
            is_installation: true,
            is_scheduled_installation: true,
            offerte_id: i.offerte_id,
            products: i.products || [],
            photos: i.photos || [],
            notes: i.notes || ''
          };
        });
      
      // Convert property installations to appointment-like format (legacy)
      const installationsAsAppointments = (installsRes.data || [])
        .filter(i => i.installation_date && (i.status === 'scheduled' || i.source_factuur_id)) // Only scheduled or from factuur
        .map(i => {
          const prop = propsRes.data.find(p => p.id === i.property_id);
          const cust = prop ? custsRes.data.find(cu => cu.id === prop.customer_id) : null;
          
          // Get installation type label
          const typeLabels = {
            'solar_panels': 'Zonnepanelen',
            'battery': 'Batterij',
            'ev_charger': 'Laadpaal',
            'inverter': 'Omvormer',
            'heat_pump': 'Warmtepomp',
            'airco': 'Airco'
          };
          const typeLabel = typeLabels[i.installation_type] || i.installation_type;
          
          return {
            id: `installation-${i.id}`,
            original_id: i.id,
            title: `Installatie: ${typeLabel} ${i.quantity > 1 ? `(${i.quantity}x)` : ''} - ${prop ? prop.street + ' ' + prop.number : ''}`,
            appointment_type: 'installation',
            date: i.installation_date,
            time: i.installation_time || '09:00',
            property_id: i.property_id,
            customer_id: i.customer_id || prop?.customer_id || '',
            assigned_to: i.installer || '',
            description: `${i.brand || ''} ${i.model || ''}\n${i.notes || ''}`.trim(),
            status: i.status || 'scheduled',
            is_installation: true,
            duration_minutes: 240 // Default 4 hours for installations
          };
        });
      
      // Combine appointments, cleanings and all types of installations
      const allAppointments = [...aptsRes.data, ...cleaningsAsAppointments, ...installationsAsAppointments, ...scheduledInstallationsAsAppointments];
      setAppointments(allAppointments);
      setCustomers(custsRes.data);
      setProperties(propsRes.data);
      setInstallations(installsRes.data || []);
      setUsers(usersRes.data || []);

      // Build calendar data
      const byDate = {};
      allAppointments.forEach(apt => {
        if (!byDate[apt.date]) byDate[apt.date] = [];
        byDate[apt.date].push(apt);
      });
      setCalendarData(byDate);
    } catch (error) {
      toast.error('Kon agenda niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Generate Google Calendar URL for an appointment
  const generateGoogleCalendarUrl = (appointmentData) => {
    const property = properties.find(p => p.id === appointmentData.property_id);
    const customer = customers.find(c => c.id === appointmentData.customer_id);
    const address = property 
      ? `${property.street} ${property.number}, ${property.postal_code} ${property.city}`
      : '';
    
    // Get installations for this property
    const propertyInstallations = property 
      ? installations.filter(i => i.property_id === property.id)
      : [];
    
    // Format: YYYYMMDDTHHMMSS
    const startDate = appointmentData.date.replace(/-/g, '');
    const time = appointmentData.time || '09:00';
    const [hours, minutes] = time.split(':').map(s => s.padStart(2, '0'));
    const startDateTime = `${startDate}T${hours}${minutes}00`;
    
    // Calculate end time
    const duration = appointmentData.duration_minutes || 60;
    let endHour = parseInt(hours) + Math.floor(duration / 60);
    let endMin = parseInt(minutes) + (duration % 60);
    if (endMin >= 60) {
      endHour += 1;
      endMin -= 60;
    }
    const endDateTime = `${startDate}T${String(endHour).padStart(2, '0')}${String(endMin).padStart(2, '0')}00`;
    
    // Get type label for title
    const typeLabels = {
      cleaning: '🧹 Reiniging',
      sales: '👥 Sales',
      installation: '🔧 Installatie',
      electricity: '⚡ Elektriciteit',
      intern: '🏢 Intern'
    };
    const typePrefix = typeLabels[appointmentData.appointment_type] || '';
    
    // Build title with type prefix
    const title = typePrefix ? `${typePrefix}: ${appointmentData.title}` : appointmentData.title;
    
    // Build description with customer info
    let description = `Type: ${APPOINTMENT_TYPES[appointmentData.appointment_type]?.label || appointmentData.appointment_type}\n\n`;
    if (customer) {
      description += `Klant: ${customer.first_name} ${customer.last_name}\n`;
      if (customer.phone) description += `Tel: ${formatPhoneNumber(customer.phone)}\n`;
      if (customer.email) description += `Email: ${customer.email}\n`;
    }
    
    // Add installation info
    if (propertyInstallations.length > 0) {
      description += '\n--- INSTALLATIES ---\n';
      propertyInstallations.forEach(inst => {
        if (inst.installation_type === 'solar_panels') {
          const totalKwp = inst.number_of_panels && inst.panel_wattage 
            ? ((inst.number_of_panels * inst.panel_wattage) / 1000).toFixed(2)
            : null;
          description += `☀️ Zonnepanelen: ${inst.number_of_panels || '?'} stuks`;
          if (inst.panel_wattage) description += ` (${inst.panel_wattage}Wp)`;
          if (totalKwp) description += ` = ${totalKwp} kWp`;
          if (inst.brand) description += ` - ${inst.brand}`;
          description += '\n';
        } else if (inst.installation_type === 'battery') {
          description += `🔋 Batterij: ${inst.brand || 'Onbekend'}`;
          if (inst.battery_capacity) description += ` ${inst.battery_capacity}`;
          description += '\n';
        } else if (inst.installation_type === 'ev_charger') {
          description += `🔌 Laadpaal: ${inst.brand || 'Onbekend'}`;
          if (inst.charger_power) description += ` ${inst.charger_power}`;
          description += '\n';
        }
      });
    }
    
    // Add original description/notes
    if (appointmentData.description) {
      description += `\n--- NOTITIES ---\n${appointmentData.description}\n`;
    }
    
    if (appointmentData.created_by_name) {
      description += `\nAangemaakt door: ${appointmentData.created_by_name}`;
    }
    
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      dates: `${startDateTime}/${endDateTime}`,
      details: description,
      location: address,
      ctz: 'Europe/Brussels'
    });
    
    // For electricity appointments, use the specific calendar
    // Note: This requires the user to have access to that shared calendar
    if (appointmentData.appointment_type === 'electricity') {
      params.append('src', '1394b4194fdb0f369eee7f5189e042fdc9a087a1d1db7658e993c47f463ba0f5@group.calendar.google.com');
    }
    
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  // Handle sync cleaning to Google Calendar - opens popup first
  const handleSyncCleaningToGoogle = (cleaningAppointment, customer, property) => {
    if (!property) {
      toast.error('Geen adresgegevens beschikbaar');
      return;
    }
    
    // Store the data and open popup
    setSelectedCleaningForCalendar(cleaningAppointment);
    setSelectedCleaningCustomer(customer);
    setSelectedCleaningProperty(property);
    setOpenGoogleCalendarAfter(true); // Open Google Calendar after
    setEmailAction('queue'); // Default to queue
    setCalendarPopupOpen(true);
  };

  // Handle "Mail maken" from dropdown menu (no Google Calendar)
  const handleSendCleaningMail = (cleaningAppointment, customer, property) => {
    if (!property) {
      toast.error('Geen adres gevonden voor deze reiniging');
      return;
    }
    
    setSelectedCleaningForCalendar(cleaningAppointment);
    setSelectedCleaningCustomer(customer);
    setSelectedCleaningProperty(property);
    setOpenGoogleCalendarAfter(false); // Don't open Google Calendar
    setEmailAction('queue'); // Default to queue
    setCalendarPopupOpen(true);
  };

  // Get panel count for a property
  const getPanelCountForProperty = (propertyId) => {
    const propertyInstallations = installations.filter(i => 
      i.property_id === propertyId && 
      i.installation_type === 'solar_panels'
    );
    return propertyInstallations.reduce((sum, inst) => sum + (inst.number_of_panels || 0), 0);
  };

  // Handle the confirmation popup submit for cleaning
  const handleCalendarConfirm = async () => {
    if (!selectedCleaningForCalendar || !selectedCleaningProperty) return;
    
    const property = selectedCleaningProperty;
    const customer = selectedCleaningCustomer;
    const cleaningAppointment = selectedCleaningForCalendar;
    
    // If user wants to send or queue confirmation email
    if (emailAction !== 'none') {
      setSendingEmail(true);
      try {
        const originalCleaningId = cleaningAppointment.original_cleaning?.id || 
                                    cleaningAppointment.id?.replace('cleaning-', '');
        
        // Queue the email via CRM
        const response = await cleaningAPI.sendConfirmationEmail({
          cleaning_id: originalCleaningId,
          property_id: property.id,
          cleaning_date: cleaningAppointment.date,
          cleaning_time: cleaningAppointment.time || '09:00',
          send_confirmation_email: true
        });
        
        if (emailAction === 'send' && response.email_id) {
          // Send the queued email immediately
          try {
            await fetch(`${API_URL}/email-queue/${response.email_id}/send`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            });
            toast.success('Bevestigingsmail verzonden!');
          } catch (sendError) {
            console.error('Error sending email:', sendError);
            toast.success('Mail klaargezet in wachtrij (verzenden mislukt)');
          }
        } else {
          toast.success('Bevestigingsmail klaargezet in wachtrij');
        }
      } catch (error) {
        console.error('Error queueing confirmation email:', error);
        toast.error('Kon bevestigingsmail niet klaarzetten');
      } finally {
        setSendingEmail(false);
      }
    }
    
    // Close popup 
    setCalendarPopupOpen(false);
    
    // Only open Google Calendar if we came from the sync button
    if (openGoogleCalendarAfter) {
      const panelCount = getPanelCountForProperty(property.id);
      
      // Parse date and time from the cleaning appointment
      const date = cleaningAppointment.date;
      const time = cleaningAppointment.time || '09:00';
      
      // Format for Google Calendar (YYYYMMDDTHHMMSS)
      const startDate = date.replace(/-/g, '');
      const [hours, minutes] = time.split(':').map(s => s.padStart(2, '0'));
      const startDateTime = `${startDate}T${hours}${minutes}00`;
      
      // End time is 2 hours later
      let endHour = parseInt(hours) + 2;
      let endMin = parseInt(minutes);
      if (endHour >= 24) {
        endHour = 23;
        endMin = 59;
      }
      const endDateTime = `${startDate}T${String(endHour).padStart(2, '0')}${String(endMin).padStart(2, '0')}00`;
      
      // Build title
      const title = `Reiniging - ${customer?.first_name || ''} ${customer?.last_name || ''}`;
      
      // Build address
      const address = `${property.street} ${property.number}, ${property.postal_code} ${property.city}`;
      
      // Build description
      let description = 'Zonnepanelen reiniging\n\n';
      if (panelCount > 0) {
        description += `🔆 ${panelCount} zonnepanelen te reinigen\n\n`;
      }
      if (customer) {
        description += `Klant: ${customer.first_name} ${customer.last_name}\n`;
        if (customer.phone) description += `Tel: ${formatPhoneNumber(customer.phone)}\n`;
        if (customer.email) description += `Email: ${customer.email}\n`;
      }
      description += `\nAdres: ${address}`;
      
      if (cleaningAppointment.original_cleaning?.performed_by) {
        description += `\n\nUitgevoerd door: ${cleaningAppointment.original_cleaning.performed_by}`;
      }
      if (cleaningAppointment.original_cleaning?.notes) {
        description += `\n\nNotities: ${cleaningAppointment.original_cleaning.notes}`;
      }
      
      const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: title,
        dates: `${startDateTime}/${endDateTime}`,
        details: description,
        location: address,
        ctz: 'Europe/Brussels'
      });
      
      window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank');
      toast.success('Google Calendar geopend');
    }
    
    // Clear selection
    setSelectedCleaningForCalendar(null);
    setSelectedCleaningCustomer(null);
    setSelectedCleaningProperty(null);
    setOpenGoogleCalendarAfter(false);
  };

  // Mark cleaning as complete (available to cleaners)
  const handleMarkCleaningComplete = async (cleaningId) => {
    try {
      await cleaningAPI.markComplete(cleaningId);
      toast.success('Reiniging gemarkeerd als voltooid');
      fetchData();
    } catch (error) {
      console.error('Error marking cleaning complete:', error);
      toast.error('Kon reiniging niet als voltooid markeren');
    }
  };

  // Open cancel dialog
  const handleOpenCancelDialog = (cleaningId) => {
    setCleaningToCancel(cleaningId);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  // Mark cleaning as cancelled
  const handleMarkCleaningCancelled = async () => {
    if (!cleaningToCancel) return;
    
    try {
      await cleaningAPI.markCancelled(cleaningToCancel, cancelReason || 'Niet voltooid');
      toast.success('Reiniging gemarkeerd als niet voltooid');
      setCancelDialogOpen(false);
      setCleaningToCancel(null);
      setCancelReason('');
      fetchData();
    } catch (error) {
      console.error('Error marking cleaning cancelled:', error);
      toast.error('Kon reiniging niet als geannuleerd markeren');
    }
  };

  // Reschedule cleaning (reset to scheduled)
  const handleRescheduleCleaning = async (cleaningId) => {
    try {
      await cleaningAPI.reschedule(cleaningId);
      toast.success('Reiniging opnieuw ingepland');
      fetchData();
    } catch (error) {
      console.error('Error rescheduling cleaning:', error);
      toast.error('Kon reiniging niet opnieuw inplannen');
    }
  };

  // Open photo upload dialog
  const handleOpenPhotoUpload = (propertyId, propertyName, cleaningId = null) => {
    setPhotoUploadPropertyId(propertyId);
    setPhotoUploadCleaningId(cleaningId);
    setPhotoUploadPropertyName(propertyName);
    setPhotoFile(null);
    setPhotoDescription('');
    setPhotoUploadOpen(true);
  };

  // Upload photo
  const handleUploadPhoto = async () => {
    if (!photoFile || !photoUploadPropertyId) return;
    
    setUploadingPhoto(true);
    try {
      const result = await cleanerAPI.uploadPhoto(photoUploadPropertyId, photoFile, photoDescription, photoUploadCleaningId);
      if (result.data?.google_drive_url) {
        toast.success('Foto geüpload en opgeslagen in Google Drive');
      } else {
        toast.success('Foto succesvol geüpload');
      }
      setPhotoUploadOpen(false);
      setPhotoFile(null);
      setPhotoDescription('');
      setPhotoUploadCleaningId(null);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Kon foto niet uploaden');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Open edit cleaning dialog
  const handleOpenEditCleaning = (appointment) => {
    // Get the original cleaning ID
    const cleaningId = appointment.original_id || appointment.id?.replace('cleaning-', '') || appointment.original_cleaning?.id;
    
    // Parse existing date and time
    let existingDate = appointment.date || appointment.cleaning_date || '';
    let existingTime = appointment.time || '09:00';
    if (existingDate.includes('T')) {
      const parts = existingDate.split('T');
      existingDate = parts[0];
      existingTime = parts[1]?.substring(0, 5) || '09:00';
    }
    
    setCleaningToEditData({
      ...appointment,
      cleaningId: cleaningId
    });
    setEditCleaningData({
      cleaning_date: existingDate,
      cleaning_time: existingTime,
      notes: appointment.original_cleaning?.notes || appointment.description || '',
      send_confirmation: false
    });
    setEditCleaningOpen(true);
  };

  // Handle edit cleaning submit
  const handleEditCleaningSubmit = async () => {
    if (!cleaningToEditData || !editCleaningData.cleaning_date) {
      toast.error('Selecteer een datum');
      return;
    }

    setEditingCleaning(true);
    try {
      const newCleaningDate = `${editCleaningData.cleaning_date}T${editCleaningData.cleaning_time}`;
      const cleaningId = cleaningToEditData.cleaningId;
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cleaning/${cleaningId}/update-date`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          new_date: newCleaningDate,
          notes: editCleaningData.notes
        })
      });

      if (!response.ok) {
        throw new Error('Could not update cleaning');
      }

      // Send confirmation email if requested
      if (editCleaningData.send_confirmation) {
        try {
          await cleaningAPI.sendConfirmationEmail({
            cleaning_id: cleaningId,
            property_id: cleaningToEditData.property_id,
            cleaning_date: editCleaningData.cleaning_date,
            cleaning_time: editCleaningData.cleaning_time,
            send_confirmation_email: true
          });
          toast.success('Afspraak bijgewerkt en bevestigingsmail verzonden!');
        } catch (emailErr) {
          console.error('Email error:', emailErr);
          toast.success('Afspraak bijgewerkt (mail kon niet verzonden worden)');
        }
      } else {
        toast.success('Afspraak bijgewerkt!');
      }

      setEditCleaningOpen(false);
      setCleaningToEditData(null);
      fetchData();
    } catch (err) {
      console.error('Edit cleaning error:', err);
      toast.error('Kon afspraak niet bijwerken');
    } finally {
      setEditingCleaning(false);
    }
  };

  // Handle installation edit submit
  const handleEditInstallationSubmit = async () => {
    if (!installationToEditData || !editInstallationData.installation_date) {
      toast.error('Selecteer een datum');
      return;
    }

    setEditingInstallation(true);
    try {
      const installationId = installationToEditData.original_id || installationToEditData.id.replace('installation-', '').replace('scheduled-', '');
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scheduled-installations/${installationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          installation_date: editInstallationData.installation_date,
          installation_end_date: editInstallationData.installation_end_date || editInstallationData.installation_date,
          installation_time: editInstallationData.installation_time,
          installation_end_time: editInstallationData.installation_end_time,
          notes: editInstallationData.notes
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Could not update installation');
      }

      // Send confirmation email if requested
      if (editInstallationData.send_confirmation) {
        try {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scheduled-installations/${installationId}/send-confirmation`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ cc_emails: [] })
          });
          toast.success('Installatie bijgewerkt en bevestigingsmail klaargezet!');
        } catch (emailErr) {
          console.error('Email error:', emailErr);
          toast.success('Installatie bijgewerkt (mail kon niet verzonden worden)');
        }
      } else {
        toast.success('Installatie bijgewerkt!');
      }

      setEditInstallationOpen(false);
      setInstallationToEditData(null);
      fetchData();
    } catch (err) {
      console.error('Edit installation error:', err);
      toast.error(`Kon installatie niet bijwerken: ${err.message}`);
    } finally {
      setEditingInstallation(false);
    }
  };

  const handleCreate = async (data) => {
    setSaving(true);
    try {
      const { emailAction, ccEmails, pendingPhotos, ...appointmentData } = data;

      // Helper: upload verzamelde foto's naar het aangemaakte record
      const uploadPending = async (targetType, targetId) => {
        if (!pendingPhotos || pendingPhotos.length === 0 || !targetId) return;
        for (const file of pendingPhotos) {
          try {
            const fd = new FormData();
            fd.append('target_type', targetType);
            fd.append('target_id', targetId);
            fd.append('file', file);
            await fetch(`${API_URL}/api/appointment-photos/upload`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
              body: fd
            });
          } catch (e) {
            console.error('Foto upload mislukt:', e);
          }
        }
        if (pendingPhotos.length > 0) toast.success(`${pendingPhotos.length} foto('s) toegevoegd`);
      };

      // Installation appointments are stored as scheduled installations
      // so they appear for assigned Install/Clean users (Agenda + Installaties page)
      if (appointmentData.appointment_type === 'installation') {
        const customer = customers.find(c => c.id === appointmentData.customer_id);
        const instResponse = await scheduledInstallationAPI.create({
          customer_id: appointmentData.customer_id || null,
          property_id: appointmentData.property_id || null,
          customer_name: customer ? `${customer.first_name} ${customer.last_name}` : null,
          installation_type: 'zonnepanelen',
          installation_date: appointmentData.date,
          installation_end_date: appointmentData.end_date || null,
          installation_time: appointmentData.time || '09:00',
          installation_end_time: appointmentData.end_time || '17:00',
          description: appointmentData.title || null,
          notes: appointmentData.description || null,
          status: 'gepland',
          assigned_to: Array.isArray(appointmentData.assigned_to) ? appointmentData.assigned_to : (appointmentData.assigned_to ? [appointmentData.assigned_to] : []),
          assigned_to_names: appointmentData.assigned_to_names || (appointmentData.assigned_to_name ? [appointmentData.assigned_to_name] : [])
        });
        const newInstId = instResponse?.data?.id || instResponse?.id;
        await uploadPending('installation', newInstId);
        toast.success('Installatie ingepland');
        setDialogOpen(false);
        fetchData();
        return;
      }

      const response = await appointmentAPI.create(appointmentData);
      toast.success('Afspraak aangemaakt');
      
      // If cleaning and email action is set, send/queue email
      const appointmentId = response?.data?.id || response?.id;
      await uploadPending('appointment', appointmentId);
      if (appointmentData.appointment_type === 'cleaning' && emailAction !== 'none' && appointmentId && appointmentData.property_id) {
        try {
          // Queue the email via CRM
          const emailResponse = await cleaningAPI.sendConfirmationEmail({
            cleaning_id: appointmentId,
            property_id: appointmentData.property_id,
            cleaning_date: appointmentData.date,
            cleaning_time: appointmentData.time || '09:00',
            send_confirmation_email: true,
            cc_emails: ccEmails || []  // Include CC recipients
          });
          
          const emailId = emailResponse?.data?.email_id || emailResponse?.email_id;
          if (emailAction === 'send' && emailId) {
            // Send the queued email immediately
            try {
              await fetch(`${API_URL}/api/email-queue/${emailId}/send`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'application/json'
                }
              });
              toast.success('Bevestigingsmail verzonden!');
            } catch (sendError) {
              console.error('Error sending email:', sendError);
              toast.success('Mail klaargezet in wachtrij');
            }
          } else {
            toast.success('Bevestigingsmail klaargezet in wachtrij');
          }
        } catch (emailError) {
          console.error('Error queueing confirmation email:', emailError);
          // Don't show error - the appointment was still created
        }
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Kon afspraak niet aanmaken');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (data) => {
    setSaving(true);
    try {
      const assignedIds = Array.isArray(data.assigned_to) ? data.assigned_to : (data.assigned_to ? [data.assigned_to] : []);
      const assignedNames = data.assigned_to_names || (data.assigned_to_name ? data.assigned_to_name.split(', ').filter(Boolean) : []);

      if (editAppointment.id.startsWith('cleaning-')) {
        const cleaningId = editAppointment.original_id || editAppointment.id.replace('cleaning-', '');
        await cleaningAPI.update(cleaningId, {
          property_id: data.property_id || undefined,
          cleaning_date: data.date,
          cleaning_end_date: data.end_date || '',
          cleaning_time: data.time || '09:00',
          cleaning_end_time: data.end_time || '',
          assigned_to: assignedIds,
          assigned_to_names: assignedNames,
          notes: data.description || ''
        });
        toast.success('Reiniging bijgewerkt');
      } else if (editAppointment.id.startsWith('scheduled-installation-')) {
        const instId = editAppointment.original_id || editAppointment.id.replace('scheduled-installation-', '');
        await scheduledInstallationAPI.update(instId, {
          customer_id: data.customer_id || undefined,
          property_id: data.property_id || undefined,
          installation_date: data.date,
          installation_end_date: data.end_date || '',
          installation_time: data.time || '09:00',
          installation_end_time: data.end_time || '17:00',
          notes: data.description || '',
          assigned_to: assignedIds,
          assigned_to_names: assignedNames
        });
        toast.success('Installatie afspraak bijgewerkt');
      } else if (editAppointment.id.startsWith('installation-')) {
        const instId = editAppointment.original_id || editAppointment.id.replace('installation-', '');
        await installationAPI.update(instId, { installation_date: data.date });
        toast.success('Installatie datum bijgewerkt');
      } else {
        await appointmentAPI.update(editAppointment.id, data);
        toast.success('Afspraak bijgewerkt');
      }

      setEditAppointment(null);
      fetchData();
    } catch (error) {
      console.error('Update error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Onbekende fout';
      toast.error(`Kon afspraak niet bijwerken: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    // Open confirmation dialog instead of window.confirm
    setAppointmentToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!appointmentToDelete) return;
    
    try {
      // Check if it's a cleaning (id starts with 'cleaning-')
      if (appointmentToDelete.startsWith('cleaning-')) {
        const cleaningId = appointmentToDelete.replace('cleaning-', '');
        await cleaningAPI.delete(cleaningId);
      } else if (appointmentToDelete.startsWith('scheduled-installation-')) {
        const instId = appointmentToDelete.replace('scheduled-installation-', '');
        await scheduledInstallationAPI.delete(instId);
      } else if (appointmentToDelete.startsWith('installation-')) {
        toast.error('Deze installatie kan enkel via de Installaties pagina beheerd worden');
        setDeleteConfirmOpen(false);
        setAppointmentToDelete(null);
        return;
      } else {
        await appointmentAPI.delete(appointmentToDelete);
      }
      toast.success('Afspraak verwijderd');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Kon afspraak niet verwijderen');
    } finally {
      setDeleteConfirmOpen(false);
      setAppointmentToDelete(null);
    }
  };

  // ============ Appointment photo attachments (legplan, wateraansluiting, ...) ============
  const getPhotoTarget = (apt) => {
    if (!apt) return null;
    if (apt.is_scheduled_installation) return { type: 'installation', id: apt.original_id };
    if (apt.is_cleaning) return { type: 'cleaning', id: apt.original_id };
    if (typeof apt.id === 'string' && apt.id.startsWith('installation-')) {
      // Legacy property installaties (installations collectie)
      return { type: 'property_installation', id: apt.original_id || apt.id.replace('installation-', '') };
    }
    return { type: 'appointment', id: apt.id };
  };

  const handleAppointmentPhotoUpload = async (apt, file) => {
    const target = getPhotoTarget(apt);
    if (!target || !file) return null;
    const fd = new FormData();
    fd.append('target_type', target.type);
    fd.append('target_id', target.id);
    fd.append('file', file);
    setUploadingPhoto(true);
    try {
      const res = await fetch(`${API_URL}/api/appointment-photos/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd
      });
      if (!res.ok) throw new Error('Upload mislukt');
      const data = await res.json();
      setAppointmentDetailData(prev => prev ? { ...prev, photos: [...(prev.photos || []), data.photo] } : prev);
      toast.success('Foto toegevoegd');
      fetchData();
      return data.photo;
    } catch (err) {
      console.error('Photo upload error:', err);
      toast.error('Kon foto niet uploaden');
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleAppointmentPhotoDelete = async (apt, photoId) => {
    const target = getPhotoTarget(apt);
    if (!target) return;
    try {
      const res = await fetch(`${API_URL}/api/appointment-photos/${target.type}/${target.id}/${photoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      setAppointmentDetailData(prev => prev ? { ...prev, photos: (prev.photos || []).filter(p => p.id !== photoId) } : prev);
      toast.success('Foto verwijderd');
      fetchData();
      return true;
    } catch (err) {
      console.error('Photo delete error:', err);
      toast.error('Kon foto niet verwijderen');
      return false;
    }
  };


  const handleStatusChange = async (id, newStatus) => {
    try {
      const apt = appointments.find(a => a.id === id);
      if (apt) {
        await appointmentAPI.update(id, { ...apt, status: newStatus });
        toast.success('Status bijgewerkt');
        fetchData();
      }
    } catch (error) {
      toast.error('Kon status niet bijwerken');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, appointment) => {
    // Don't allow dragging read-only appointments
    if (appointment.read_only || appointment.is_google_event) {
      e.preventDefault();
      return;
    }
    setDraggedAppointment(appointment);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', appointment.id);
    // Add a slight delay to show the drag effect
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedAppointment(null);
    setDragOverTarget(null);
  };

  const handleDragOver = (e, targetDate, targetHour = null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const targetKey = targetHour !== null ? `${targetDate}-${targetHour}` : targetDate;
    setDragOverTarget(targetKey);
  };

  const handleDragLeave = () => {
    setDragOverTarget(null);
  };

  const handleDrop = async (e, targetDate, targetHour = null) => {
    e.preventDefault();
    setDragOverTarget(null);
    
    if (!draggedAppointment) return;
    
    const apt = draggedAppointment;
    const newDate = targetDate;
    const newTime = targetHour !== null ? `${String(targetHour).padStart(2, '0')}:00` : apt.time;
    
    // Don't update if nothing changed
    if (apt.date === newDate && (targetHour === null || apt.time === newTime)) {
      setDraggedAppointment(null);
      return;
    }
    
    try {
      // Handle different appointment types
      if (apt.id.startsWith('cleaning-')) {
        const cleaningId = apt.original_id || apt.id.replace('cleaning-', '');
        await cleaningAPI.update(cleaningId, {
          cleaning_date: newDate,
          cleaning_time: newTime
        });
        toast.success('Reiniging verplaatst');
      } else if (apt.id.startsWith('scheduled-')) {
        const installId = apt.original_id || apt.id.replace('scheduled-', '');
        const schedPayload = { installation_date: newDate, installation_time: newTime };
        if (apt.end_date) {
          const deltaDays = Math.round((new Date(newDate) - new Date(apt.date)) / 86400000);
          const d = new Date(apt.end_date + 'T00:00:00');
          d.setDate(d.getDate() + deltaDays);
          schedPayload.installation_end_date = formatLocalDate(d);
        }
        await scheduledInstallationAPI.update(installId, schedPayload);
        toast.success('Geplande installatie verplaatst');
      } else if (apt.id.startsWith('installation-')) {
        // Regular installations from the installations collection
        const installId = apt.original_id || apt.id.replace('installation-', '');
        const instPayload = { installation_date: newDate, installation_time: newTime };
        if (apt.end_date) {
          const deltaDays = Math.round((new Date(newDate) - new Date(apt.date)) / 86400000);
          const d = new Date(apt.end_date + 'T00:00:00');
          d.setDate(d.getDate() + deltaDays);
          instPayload.installation_end_date = formatLocalDate(d);
        }
        await installationAPI.update(installId, instPayload);
        toast.success('Installatie verplaatst');
      } else {
        // Regular appointment — schuif end_date mee op zodat de afspraak zichtbaar blijft
        let newEndDate = apt.end_date || null;
        if (newEndDate) {
          const deltaDays = Math.round((new Date(newDate) - new Date(apt.date)) / 86400000);
          const d = new Date(newEndDate + 'T00:00:00');
          d.setDate(d.getDate() + deltaDays);
          newEndDate = formatLocalDate(d);
        }
        await appointmentAPI.update(apt.id, {
          ...apt,
          date: newDate,
          end_date: newEndDate,
          time: newTime
        });
        toast.success('Afspraak verplaatst');
      }
      
      fetchData();
    } catch (error) {
      console.error('Drag drop error:', error);
      toast.error('Kon afspraak niet verplaatsen');
    } finally {
      setDraggedAppointment(null);
    }
  };

  const selectedDateStr = formatLocalDate(selectedDate);
  
  // Filter appointments that are on the selected date (including multi-day appointments)
  const appointmentsForSelectedDate = appointments.filter(a => {
    const startDate = a.date;
    const endDate = a.end_date || a.date;
    return selectedDateStr >= startDate && selectedDateStr <= endDate;
  });

  const filteredAppointments = activeTab === 'all' 
    ? appointmentsForSelectedDate 
    : appointmentsForSelectedDate.filter(a => a.appointment_type === activeTab);

  // Get dates with appointments for calendar highlighting (including multi-day ranges)
  const datesWithAppointments = useMemo(() => {
    const dates = new Set();
    appointments.forEach(a => {
      const startDate = parse(a.date, 'yyyy-MM-dd', new Date());
      const endDate = a.end_date ? parse(a.end_date, 'yyyy-MM-dd', new Date()) : startDate;
      
      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        dates.add(formatLocalDate(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
    });
    return Array.from(dates).map(d => {
      const [year, month, day] = d.split('-').map(Number);
      return new Date(year, month - 1, day);
    });
  }, [appointments]);

  // Helper function to get appointments for a specific date (for hover tooltip)
  // Includes multi-day appointments that span this date
  const getAppointmentsForDate = (date) => {
    const dateStr = formatLocalDate(date);
    return appointments.filter(a => {
      const startDate = a.date;
      const endDate = a.end_date || a.date;
      return dateStr >= startDate && dateStr <= endDate;
    });
  };

  // Helper to format duration
  const formatDuration = (minutes) => {
    if (!minutes) return '';
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}u${mins}` : `${hours}u`;
    }
    return `${minutes}min`;
  };

  // Helper to get city from appointment
  const getAppointmentCity = (appointment) => {
    if (appointment.property_id) {
      const prop = properties.find(p => p.id === appointment.property_id);
      if (prop?.city) return prop.city;
    }
    if (appointment.customer_id) {
      const cust = customers.find(c => c.id === appointment.customer_id);
      if (cust?.city) return cust.city;
    }
    return null;
  };

  // Custom day content component with hover tooltip
  const DayWithTooltip = ({ date, ...props }) => {
    const dayAppointments = getAppointmentsForDate(date);
    const hasAppointments = dayAppointments.length > 0;
    
    if (!hasAppointments) {
      return <span>{date.getDate()}</span>;
    }

    // Group by type
    const cleanings = dayAppointments.filter(a => a.appointment_type === 'cleaning');
    const sales = dayAppointments.filter(a => a.appointment_type === 'sales');
    const installations = dayAppointments.filter(a => a.appointment_type === 'installation');
    const electricityAppts = dayAppointments.filter(a => a.appointment_type === 'electricity');
    const internAppts = dayAppointments.filter(a => a.appointment_type === 'intern');

    // Render appointment info with duration and city
    const renderAppointment = (a, i) => {
      const duration = formatDuration(a.duration_minutes);
      const city = getAppointmentCity(a);
      const timeStr = a.all_day ? 'Hele dag' : (a.end_time ? `${a.time}-${a.end_time}` : a.time);
      return (
        <div key={i} className="text-xs pl-4 text-muted-foreground space-y-0.5">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-medium">{timeStr}</span>
            {duration && <span className="text-muted-foreground/70">({duration})</span>}
            <span>-</span>
            <span>{a.customer_name || a.title}</span>
            {city && <span className="text-blue-400/80">• {city}</span>}
          </div>
          {a.assigned_to_name && <div className="text-primary/70">→ {a.assigned_to_name}</div>}
        </div>
      );
    };

    return (
      <HoverCard openDelay={200} closeDelay={100}>
        <HoverCardTrigger asChild>
          <span className="cursor-pointer">{date.getDate()}</span>
        </HoverCardTrigger>
        <HoverCardContent className="w-80 p-3" side="right" align="start">
          <div className="space-y-2">
            <p className="font-semibold text-sm border-b pb-1">
              {format(date, 'd MMMM yyyy', { locale: nl })}
            </p>
            <p className="text-xs text-muted-foreground">{dayAppointments.length} afspraken</p>
            
            {/* Reinigingen */}
            {cleanings.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-purple-400">
                  <Sparkles className="w-3 h-3" />
                  Reinigingen ({cleanings.length})
                </div>
                {cleanings.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {cleanings.length > 3 && (
                  <div className="text-xs pl-4 text-muted-foreground">+{cleanings.length - 3} meer...</div>
                )}
              </div>
            )}
            
            {/* Sales */}
            {sales.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-blue-400">
                  <Users className="w-3 h-3" />
                  Sales ({sales.length})
                </div>
                {sales.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {sales.length > 3 && (
                  <div className="text-xs pl-4 text-muted-foreground">+{sales.length - 3} meer...</div>
                )}
              </div>
            )}
            
            {/* Installaties */}
            {installations.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-amber-400">
                  <Wrench className="w-3 h-3" />
                  Installaties ({installations.length})
                </div>
                {installations.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {installations.length > 3 && (
                  <div className="text-xs pl-4 text-muted-foreground">+{installations.length - 3} meer...</div>
                )}
              </div>
            )}
            
            {/* Elektriciteit */}
            {electricityAppts.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-yellow-400">
                  <Zap className="w-3 h-3" />
                  Elektriciteit ({electricityAppts.length})
                </div>
                {electricityAppts.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {electricityAppts.length > 3 && (
                  <div className="text-xs pl-4 text-muted-foreground">+{electricityAppts.length - 3} meer...</div>
                )}
              </div>
            )}
            
            {/* Intern */}
            {internAppts.length > 0 && (
              <div className="space-y-1">
                <div className="flex items-center gap-1 text-xs font-medium text-emerald-500">
                  <Building2 className="w-3 h-3" />
                  Intern ({internAppts.length})
                </div>
                {internAppts.slice(0, 3).map((a, i) => renderAppointment(a, i))}
                {internAppts.length > 3 && (
                  <div className="text-xs pl-4 text-muted-foreground">+{internAppts.length - 3} meer...</div>
                )}
              </div>
            )}
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  };

  // Count by type for tabs
  const countByType = {
    all: appointmentsForSelectedDate.length,
    cleaning: appointmentsForSelectedDate.filter(a => a.appointment_type === 'cleaning').length,
    sales: appointmentsForSelectedDate.filter(a => a.appointment_type === 'sales').length,
    installation: appointmentsForSelectedDate.filter(a => a.appointment_type === 'installation').length,
    electricity: appointmentsForSelectedDate.filter(a => a.appointment_type === 'electricity').length,
  };

  // Cleaner-specific view
  if (isCleaner) {
    const cleaningAppointments = appointments.filter(a => a.is_cleaning);
    // For cleaners: only show scheduled (not cancelled) and completed
    // Sort by date first, then by time - earliest first for scheduled
    const sortByDateTime = (a, b) => {
      // First compare dates
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      // If same date, compare times (format: "HH:mm")
      const timeA = a.time || '00:00';
      const timeB = b.time || '00:00';
      return timeA.localeCompare(timeB);
    };
    
    const _today = new Date();
    _today.setHours(0, 0, 0, 0);
    const scheduledCleanings = cleaningAppointments
      .filter(a => a.status === 'scheduled' || !a.status)
      .sort((a, b) => {
        // Eerstkomende (vandaag/toekomst) bovenaan; achterstallige (verleden) onderaan
        const aPast = new Date(a.date) < _today;
        const bPast = new Date(b.date) < _today;
        if (aPast !== bPast) return aPast ? 1 : -1;
        return sortByDateTime(a, b);
      });
    const completedCleanings = cleaningAppointments
      .filter(a => a.status === 'completed')
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    // Cancelled cleanings are handled by admin in Reiniging tab
    
    return (
      <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="cleaner-agenda-page">
        {/* Header for Cleaners - matching Installaties page style */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Reinigingen</h1>
          </div>
        </div>

        {/* Stats for Cleaners - only show pending count */}
        <div className="grid grid-cols-1 gap-3 md:gap-4">
          <Card className="bg-purple-500/10 border-purple-500/30">
            <CardContent className="p-3 md:p-4 flex items-center gap-2 md:gap-3">
              <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-purple-400" />
              <div>
                <p className="text-xl md:text-2xl font-bold">{scheduledCleanings.length}</p>
                <p className="text-xs text-muted-foreground">{t('pending')}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Card key={i} className="h-24 animate-pulse bg-muted" />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Scheduled Cleanings */}
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400" />
                {t('scheduledCleanings')} ({scheduledCleanings.length})
              </h2>
              {scheduledCleanings.length === 0 ? (
                <Card className="p-8 text-center">
                  <Sparkles className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">{t('noEventsToday')}</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {scheduledCleanings.map(apt => (
                    <CleanerAppointmentCard
                      key={apt.id}
                      appointment={apt}
                      onMarkComplete={handleMarkCleaningComplete}
                      onMarkCancelled={handleOpenCancelDialog}
                      onReschedule={handleRescheduleCleaning}
                      onUploadPhoto={handleOpenPhotoUpload}
                      t={t}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Completed Cleanings - Hidden for Install/Clean users */}
            {!isCleaner && completedCleanings.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  {t('completedCleanings')} ({completedCleanings.length})
                </h2>
                <div className="space-y-3">
                  {completedCleanings.map(apt => (
                    <CleanerAppointmentCard
                      key={apt.id}
                      appointment={apt}
                      onMarkComplete={handleMarkCleaningComplete}
                      onMarkCancelled={handleOpenCancelDialog}
                      onReschedule={handleRescheduleCleaning}
                      onUploadPhoto={handleOpenPhotoUpload}
                      t={t}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cancel Cleaning Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-500">
                <X className="w-5 h-5" />
                Reiniging Niet Voltooid
              </DialogTitle>
              <DialogDescription>
                Geef een reden waarom de reiniging niet kon doorgaan
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Reden</Label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Bijv. Klant niet thuis, slechte weersomstandigheden, toegang geblokkeerd..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => {
                  setCancelDialogOpen(false);
                  setCleaningToCancel(null);
                  setCancelReason('');
                }}
              >
                Annuleren
              </Button>
              <Button 
                variant="destructive"
                onClick={handleMarkCleaningCancelled}
                className="gap-2"
              >
                <X className="w-4 h-4" />
                Bevestigen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Photo Upload Dialog */}
        <Dialog open={photoUploadOpen} onOpenChange={setPhotoUploadOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary" />
                Foto Toevoegen
              </DialogTitle>
              <DialogDescription>
                Upload een foto voor {photoUploadPropertyName}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Foto *</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhotoFile(e.target.files[0])}
                  className="cursor-pointer"
                />
                {photoFile && (
                  <p className="text-xs text-muted-foreground">
                    Geselecteerd: {photoFile.name}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Beschrijving (optioneel)</Label>
                <Textarea
                  value={photoDescription}
                  onChange={(e) => setPhotoDescription(e.target.value)}
                  placeholder="Beschrijf de foto..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => {
                  setPhotoUploadOpen(false);
                  setPhotoFile(null);
                  setPhotoDescription('');
                }}
              >
                Annuleren
              </Button>
              <Button 
                onClick={handleUploadPhoto}
                disabled={!photoFile || uploadingPhoto}
                className="gap-2"
              >
                {uploadingPhoto ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Uploaden...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Regular view for non-cleaners
  return (
    <div className="space-y-4 md:space-y-6 animate-fade-in" data-testid="agenda-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold tracking-tight">
            {isSales ? 'Mijn Agenda' : 'Agenda'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isSales ? 'Beheer uw sales afspraken' : 'Plan en beheer afspraken'}
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-appointment-btn" size="sm" className="md:size-default">
          <Plus className="w-4 h-4 mr-1 md:mr-2" />
          <span className="hidden sm:inline">Nieuwe </span>{isSales ? 'Sales Afspraak' : 'Afspraak'}
        </Button>
      </div>

      {/* Main content - Full Google Calendar Style Layout */}
      <div className="flex gap-0 h-[calc(100vh-160px)] min-h-[500px] md:min-h-[600px] border rounded-lg overflow-hidden bg-card">
        {/* Left Sidebar - Mini Calendar & Filters (desktop only) */}
        <div className="hidden lg:flex w-64 border-r flex-col shrink-0 bg-muted/30">
          {/* Mini Calendar */}
          <div className="p-4 border-b">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              locale={nl}
              className="rounded-md w-full"
              modifiers={{
                hasAppointment: datesWithAppointments
              }}
              modifiersStyles={{
                hasAppointment: {
                  fontWeight: 'bold',
                  backgroundColor: 'hsl(var(--primary) / 0.2)',
                  borderRadius: '50%'
                }
              }}
            />
          </div>
          
          {/* Type Filters */}
          <div className="p-4 space-y-3 flex-1">
            <h3 className="text-sm font-semibold">Mijn agenda's</h3>
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer hover:bg-muted p-2 rounded-md transition-colors" data-testid="filter-cleaning-checkbox">
                <Checkbox
                  checked={typeFilters.cleaning}
                  onCheckedChange={(checked) => setTypeFilters(prev => ({ ...prev, cleaning: checked }))}
                />
                <span className="w-3 h-3 rounded-sm bg-purple-500" />
                <span className="text-sm flex-1">Reinigingen</span>
                <span className="text-xs text-muted-foreground">({appointments.filter(a => a.appointment_type === 'cleaning' && a.status === 'scheduled').length})</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer hover:bg-muted p-2 rounded-md transition-colors" data-testid="filter-sales-checkbox">
                <Checkbox
                  checked={typeFilters.sales}
                  onCheckedChange={(checked) => setTypeFilters(prev => ({ ...prev, sales: checked }))}
                />
                <span className="w-3 h-3 rounded-sm bg-blue-500" />
                <span className="text-sm flex-1">Sales</span>
                <span className="text-xs text-muted-foreground">({appointments.filter(a => a.appointment_type === 'sales' && a.status === 'scheduled').length})</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer hover:bg-muted p-2 rounded-md transition-colors" data-testid="filter-installation-checkbox">
                <Checkbox
                  checked={typeFilters.installation}
                  onCheckedChange={(checked) => setTypeFilters(prev => ({ ...prev, installation: checked }))}
                />
                <span className="w-3 h-3 rounded-sm bg-amber-500" />
                <span className="text-sm flex-1">Installaties</span>
                <span className="text-xs text-muted-foreground">({appointments.filter(a => a.appointment_type === 'installation' && ['scheduled', 'gepland', 'in_uitvoering'].includes(a.status)).length})</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer hover:bg-muted p-2 rounded-md transition-colors" data-testid="filter-electricity-checkbox">
                <Checkbox
                  checked={typeFilters.electricity}
                  onCheckedChange={(checked) => setTypeFilters(prev => ({ ...prev, electricity: checked }))}
                />
                <span className="w-3 h-3 rounded-sm bg-red-500" />
                <span className="text-sm flex-1">Elektriciteit</span>
                <span className="text-xs text-muted-foreground">({appointments.filter(a => a.appointment_type === 'electricity' && a.status === 'scheduled').length})</span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer hover:bg-muted p-2 rounded-md transition-colors" data-testid="filter-intern-checkbox">
                <Checkbox
                  checked={typeFilters.intern}
                  onCheckedChange={(checked) => setTypeFilters(prev => ({ ...prev, intern: checked }))}
                />
                <span className="w-3 h-3 rounded-sm bg-emerald-600" />
                <span className="text-sm flex-1">Intern</span>
                <span className="text-xs text-muted-foreground">({appointments.filter(a => a.appointment_type === 'intern' && a.status === 'scheduled').length})</span>
              </label>
            </div>
          </div>
        </div>
        
        {/* Main Calendar Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header with navigation */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2 sm:p-4 border-b bg-card">
            <div className="flex items-center gap-1 sm:gap-2 min-w-0">
              {/* Mobile filters trigger */}
              <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="lg:hidden shrink-0" data-testid="mobile-filters-btn" aria-label="Filters">
                    <Filter className="w-4 h-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[85vw] max-w-sm p-0 overflow-y-auto">
                  <SheetHeader className="p-4 border-b">
                    <SheetTitle>Agenda filters</SheetTitle>
                  </SheetHeader>
                  <div className="p-4 border-b">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => { if (date) { setSelectedDate(date); setMobileFiltersOpen(false); } }}
                      month={currentMonth}
                      onMonthChange={setCurrentMonth}
                      locale={nl}
                      className="rounded-md w-full"
                      modifiers={{ hasAppointment: datesWithAppointments }}
                      modifiersStyles={{
                        hasAppointment: {
                          fontWeight: 'bold',
                          backgroundColor: 'hsl(var(--primary) / 0.2)',
                          borderRadius: '50%'
                        }
                      }}
                    />
                  </div>
                  <div className="p-4 space-y-3">
                    <h3 className="text-sm font-semibold">Mijn agenda's</h3>
                    <div className="space-y-1">
                      {[
                        { key: 'cleaning', color: 'bg-purple-500', label: 'Reinigingen', statusFilter: (a) => a.status === 'scheduled' },
                        { key: 'sales', color: 'bg-blue-500', label: 'Sales', statusFilter: (a) => a.status === 'scheduled' },
                        { key: 'installation', color: 'bg-amber-500', label: 'Installaties', statusFilter: (a) => ['scheduled', 'gepland', 'in_uitvoering'].includes(a.status) },
                        { key: 'electricity', color: 'bg-red-500', label: 'Elektriciteit', statusFilter: (a) => a.status === 'scheduled' },
                        { key: 'intern', color: 'bg-emerald-600', label: 'Intern', statusFilter: (a) => a.status === 'scheduled' },
                      ].map((f) => (
                        <label key={f.key} className="flex items-center gap-3 cursor-pointer hover:bg-muted p-2 rounded-md transition-colors">
                          <Checkbox
                            checked={typeFilters[f.key]}
                            onCheckedChange={(checked) => setTypeFilters(prev => ({ ...prev, [f.key]: checked }))}
                          />
                          <span className={`w-3 h-3 rounded-sm ${f.color}`} />
                          <span className="text-sm flex-1">{f.label}</span>
                          <span className="text-xs text-muted-foreground">({appointments.filter(a => a.appointment_type === f.key && f.statusFilter(a)).length})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              <Button variant="outline" size="sm" onClick={() => setSelectedDate(new Date())}>
                Vandaag
              </Button>
              <div className="flex items-center">
                <Button variant="ghost" size="icon" onClick={() => {
                  const newDate = new Date(selectedDate);
                  if (viewMode === 'week') {
                    newDate.setDate(newDate.getDate() - 7);
                  } else if (viewMode === 'agenda') {
                    newDate.setDate(newDate.getDate() - 7);
                  } else {
                    newDate.setMonth(newDate.getMonth() - 1);
                  }
                  setSelectedDate(newDate);
                  setCurrentMonth(newDate);
                }}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => {
                  const newDate = new Date(selectedDate);
                  if (viewMode === 'week') {
                    newDate.setDate(newDate.getDate() + 7);
                  } else if (viewMode === 'agenda') {
                    newDate.setDate(newDate.getDate() + 7);
                  } else {
                    newDate.setMonth(newDate.getMonth() + 1);
                  }
                  setSelectedDate(newDate);
                  setCurrentMonth(newDate);
                }}>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
              <h2 className="text-base sm:text-xl font-semibold ml-1 sm:ml-2 truncate">
                {viewMode === 'week' 
                  ? format(startOfWeek(selectedDate, { weekStartsOn: 1 }), 'MMMM yyyy', { locale: nl })
                  : format(selectedDate, 'MMMM yyyy', { locale: nl })
                }
              </h2>
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 border rounded-lg p-1 self-start sm:self-auto">
              <Button
                variant={viewMode === 'agenda' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 px-2 sm:px-3"
                onClick={() => setViewMode('agenda')}
                data-testid="viewmode-agenda-btn"
              >
                <List className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Lijst</span>
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 px-2 sm:px-3"
                onClick={() => setViewMode('calendar')}
                data-testid="viewmode-calendar-btn"
              >
                <LayoutGrid className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Maand</span>
              </Button>
              <Button
                variant={viewMode === 'week' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 px-2 sm:px-3"
                onClick={() => setViewMode('week')}
                data-testid="viewmode-week-btn"
              >
                <CalendarDays className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Week</span>
              </Button>
            </div>
          </div>
          
          {/* Calendar Grid - Agenda (list), Month, or Week View */}
          {viewMode === 'agenda' ? (
            /* Agenda List View - Mobile-first day-grouped list */
            <div className="flex-1 overflow-auto p-3 space-y-4" data-testid="calendar-agenda-list">
              {(() => {
                // Show 8 weeks from the currently selected date
                const rangeStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
                const rangeEnd = addDays(rangeStart, 55);
                const days = eachDayOfInterval({ start: rangeStart, end: rangeEnd });
                const daysWithAppts = days
                  .map((day) => {
                    const dayStr = format(day, 'yyyy-MM-dd');
                    const dayAppointments = appointments
                      .filter(a => isDayInAppointmentRange(dayStr, a) && typeFilters[a.appointment_type] !== false)
                      .sort((a, b) => (a.time || '').localeCompare(b.time || ''));
                    return { day, dayStr, dayAppointments };
                  })
                  .filter(({ dayAppointments }) => dayAppointments.length > 0);

                if (daysWithAppts.length === 0) {
                  return (
                    <div className="text-center py-12 text-muted-foreground">
                      <CalendarIcon className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p className="text-sm">Geen afspraken in deze periode.</p>
                      <Button variant="outline" size="sm" className="mt-4" onClick={() => setDialogOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" /> Afspraak toevoegen
                      </Button>
                    </div>
                  );
                }

                return daysWithAppts.map(({ day, dayStr, dayAppointments }) => {
                  const todayCheck = isToday(day);
                  return (
                    <div key={dayStr} data-testid={`agenda-day-${dayStr}`}>
                      {/* Sticky day header */}
                      <div className={`sticky top-0 z-10 bg-card/95 backdrop-blur-sm -mx-3 px-3 py-2 border-b flex items-center justify-between ${todayCheck ? 'text-primary' : ''}`}>
                        <div className="flex items-baseline gap-2">
                          <span className={`text-2xl font-bold ${todayCheck ? 'bg-primary text-primary-foreground rounded-full w-9 h-9 flex items-center justify-center text-lg' : ''}`}>
                            {format(day, 'd')}
                          </span>
                          <div className="flex flex-col leading-tight">
                            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              {format(day, 'EEEE', { locale: nl })}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {format(day, 'MMMM yyyy', { locale: nl })}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {dayAppointments.length} afspra{dayAppointments.length === 1 ? 'ak' : 'ken'}
                        </span>
                      </div>

                      {/* Appointment cards */}
                      <div className="space-y-2 pt-2">
                        {dayAppointments.map((apt) => {
                          const typeConfig = {
                            cleaning: { bg: 'bg-purple-500', text: 'text-white', label: 'Reiniging', icon: Sparkles },
                            sales: { bg: 'bg-blue-500', text: 'text-white', label: 'Sales', icon: Users },
                            installation: { bg: 'bg-amber-500', text: 'text-white', label: 'Installatie', icon: Wrench },
                            electricity: { bg: 'bg-red-500', text: 'text-white', label: 'Elektriciteit', icon: Zap },
                            intern: { bg: 'bg-emerald-600', text: 'text-white', label: 'Intern', icon: Building2 },
                          }[apt.appointment_type] || { bg: 'bg-gray-500', text: 'text-white', label: 'Afspraak', icon: CalendarIcon };
                          const Icon = typeConfig.icon;
                          const customer = customers.find(c => c.id === apt.customer_id);
                          const property = properties.find(p => p.id === apt.property_id);
                          const isMultiDay = apt.end_date && apt.end_date !== apt.date;
                          // Compute day number within multi-day range for progress hint (dag X / N)
                          let multiDayInfo = '';
                          if (isMultiDay) {
                            const start = parse(apt.date, 'yyyy-MM-dd', new Date());
                            const end = parse(apt.end_date, 'yyyy-MM-dd', new Date());
                            const cur = parse(dayStr, 'yyyy-MM-dd', new Date());
                            const totalDays = Math.round((end - start) / 86400000) + 1;
                            const currentDay = Math.round((cur - start) / 86400000) + 1;
                            multiDayInfo = `Dag ${currentDay} van ${totalDays}`;
                          }
                          const endTime = apt.end_time || (apt.time && apt.duration_minutes ? (() => {
                            const [h, m] = apt.time.split(':').map(Number);
                            const end = h * 60 + m + apt.duration_minutes;
                            return `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`;
                          })() : '');

                          return (
                            <button
                              key={`${apt.id}-${dayStr}`}
                              type="button"
                              onClick={() => {
                                setAppointmentDetailData(apt);
                                setAppointmentDetailOpen(true);
                              }}
                              className="w-full flex items-stretch gap-0 bg-card border rounded-lg overflow-hidden hover:shadow-md active:scale-[0.99] transition-all text-left"
                              data-testid={`agenda-appointment-${apt.id}`}
                            >
                              {/* Left color bar */}
                              <div className={`w-1.5 ${typeConfig.bg} shrink-0`} />
                              <div className="flex-1 min-w-0 p-3">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div className={`${typeConfig.bg} ${typeConfig.text} p-1.5 rounded-md shrink-0`}>
                                      <Icon className="w-3.5 h-3.5" />
                                    </div>
                                    <h3 className="font-semibold text-sm truncate">{apt.title}</h3>
                                  </div>
                                  <Badge variant="outline" className="text-[10px] shrink-0 px-1.5 py-0">{typeConfig.label}</Badge>
                                </div>
                                {isMultiDay && (
                                  <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1">
                                    <CalendarDays className="w-3 h-3 shrink-0" />
                                    <span>
                                      {format(parse(apt.date, 'yyyy-MM-dd', new Date()), 'd MMM', { locale: nl })} – {format(parse(apt.end_date, 'yyyy-MM-dd', new Date()), 'd MMM', { locale: nl })}
                                      <span className="ml-1.5 opacity-70">· {multiDayInfo}</span>
                                    </span>
                                  </div>
                                )}
                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                                  <Clock className="w-3 h-3 shrink-0" />
                                  <span>
                                    {apt.time || '-'}
                                    {endTime && ` – ${endTime}`}
                                  </span>
                                </div>
                                {customer && (
                                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                                    <User className="w-3 h-3 shrink-0" />
                                    <span className="truncate">{customer.first_name} {customer.last_name}</span>
                                  </div>
                                )}
                                {property && (
                                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                    <MapPin className="w-3 h-3 shrink-0" />
                                    <span className="truncate">{property.street} {property.number}, {property.city}</span>
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : viewMode === 'calendar' ? (
            /* Month View Grid */
            <div className="flex-1 overflow-auto p-2" data-testid="calendar-monthly-grid">
              {/* Day headers */}
              <div className="grid grid-cols-7 mb-1">
                {['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'].map((day) => (
                  <div key={day} className="p-2 text-center text-xs font-medium text-muted-foreground uppercase">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Month grid */}
              <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
                {(() => {
                  const monthStart = startOfMonth(selectedDate);
                  const monthEnd = endOfMonth(selectedDate);
                  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
                  const endDate = addDays(startOfWeek(addDays(monthEnd, 6), { weekStartsOn: 1 }), 6);
                  const days = eachDayOfInterval({ start: startDate, end: endDate });
                  
                  return days.map((day) => {
                    const dayStr = format(day, 'yyyy-MM-dd');
                    const dayAppointments = appointments
                      .filter(a => isDayInAppointmentRange(dayStr, a) && typeFilters[a.appointment_type] !== false)
                      .sort((a, b) => {
                        // Multi-day appointments first, then by time
                        const aMulti = a.end_date && a.end_date !== a.date ? 0 : 1;
                        const bMulti = b.end_date && b.end_date !== b.date ? 0 : 1;
                        if (aMulti !== bMulti) return aMulti - bMulti;
                        return (a.time || '').localeCompare(b.time || '');
                      });
                    const isCurrentMonth = isSameMonth(day, selectedDate);
                    const isSelected = isSameDay(day, selectedDate);
                    const todayCheck = isToday(day);
                    const isDragOver = dragOverTarget === dayStr;
                    const isMenuOpen = dayMenuOpen && dayMenuDate === dayStr;
                    
                    return (
                      <Popover 
                        key={dayStr} 
                        open={isMenuOpen} 
                        onOpenChange={(open) => {
                          setDayMenuOpen(open);
                          if (open) setDayMenuDate(dayStr);
                        }}
                      >
                        <PopoverTrigger asChild>
                          <div
                            className={`min-h-[70px] sm:min-h-[100px] bg-card p-1 cursor-pointer transition-all hover:bg-muted/50 ${
                              !isCurrentMonth ? 'opacity-40' : ''
                            } ${isSelected ? 'ring-2 ring-primary ring-inset' : ''} ${
                              todayCheck ? 'bg-primary/5' : ''
                            } ${isDragOver ? 'bg-primary/20 ring-2 ring-primary ring-dashed' : ''}`}
                            onClick={(e) => {
                              // Don't open menu if we're dragging
                              if (draggedAppointment) return;
                              setSelectedDate(day);
                            }}
                            onDragOver={(e) => handleDragOver(e, dayStr)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, dayStr)}
                            data-testid={`month-day-${dayStr}`}
                          >
                        {/* Day number */}
                        <div className={`text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full ${
                          todayCheck 
                            ? 'bg-primary text-primary-foreground' 
                            : isSelected 
                              ? 'text-primary' 
                              : ''
                        }`}>
                          {format(day, 'd')}
                        </div>
                        
                        {/* Appointments for this day */}
                        <div className="space-y-0.5">
                          {dayAppointments.slice(0, 3).map((apt) => {
                            const typeConfig = {
                              cleaning: { bg: 'bg-purple-500', text: 'text-white' },
                              sales: { bg: 'bg-blue-500', text: 'text-white' },
                              installation: { bg: 'bg-amber-500', text: 'text-white' },
                              electricity: { bg: 'bg-red-500', text: 'text-white' },
                              intern: { bg: 'bg-emerald-600', text: 'text-white' },
                            }[apt.appointment_type] || { bg: 'bg-gray-500', text: 'text-white' };
                            const spanPos = getSpanPosition(dayStr, apt);
                            const isMultiDay = spanPos !== 'single';
                            // Only allow dragging from the start day of a multi-day appointment
                            const isDraggable = !apt.read_only && !apt.is_google_event && (!isMultiDay || spanPos === 'start');
                            const isAptMenuOpen = appointmentMenuOpen && appointmentMenuData?.id === apt.id;
                            // Show title only on start day OR on Monday (week wrap) for continuity
                            const isMonday = day.getDay() === 1;
                            const showTitle = spanPos === 'start' || spanPos === 'single' || isMonday;
                            // Border-radius: only round the outer edges of the range for Google-Calendar look
                            let radiusClass = 'rounded';
                            if (spanPos === 'start') radiusClass = 'rounded-l rounded-r-none -mr-1';
                            else if (spanPos === 'middle') radiusClass = 'rounded-none -mx-1';
                            else if (spanPos === 'end') radiusClass = 'rounded-r rounded-l-none -ml-1';

                            return (
                              <Popover 
                                key={`${apt.id}-${dayStr}`}
                                open={isAptMenuOpen}
                                onOpenChange={(open) => {
                                  if (draggedAppointment) return;
                                  setAppointmentMenuOpen(open);
                                  if (open) setAppointmentMenuData(apt);
                                }}
                              >
                                <PopoverTrigger asChild>
                                  <div
                                    draggable={isDraggable}
                                    onDragStart={(e) => {
                                      setAppointmentMenuOpen(false);
                                      handleDragStart(e, apt);
                                    }}
                                    onDragEnd={handleDragEnd}
                                    className={`${typeConfig.bg} ${typeConfig.text} text-[10px] px-1 py-0.5 ${radiusClass} truncate hover:opacity-80 ${
                                      isDraggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                                    } ${draggedAppointment?.id === apt.id ? 'opacity-50' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                    title={`${apt.time || ''} ${apt.title}${isMultiDay ? ` (${apt.date} → ${apt.end_date})` : ''}${isDraggable ? ' (sleep om te verplaatsen)' : ''}`}
                                    data-testid={`month-appointment-${apt.id}-${dayStr}`}
                                  >
                                    {showTitle ? (
                                      <>
                                        {!isMultiDay && apt.time && <span className="font-medium">{apt.time.slice(0, 5)} </span>}
                                        {apt.title}
                                      </>
                                    ) : (
                                      <span className="opacity-0">·</span>
                                    )}
                                  </div>
                                </PopoverTrigger>
                                <PopoverContent className="w-48 p-1" align="start" onClick={(e) => e.stopPropagation()}>
                                  <div className="space-y-0.5">
                                    <Button
                                      variant="ghost"
                                      className="w-full justify-start h-8 text-sm"
                                      onClick={() => {
                                        setAppointmentDetailData(apt);
                                        setAppointmentDetailOpen(true);
                                        setAppointmentMenuOpen(false);
                                      }}
                                    >
                                      <Eye className="w-4 h-4 mr-2" />
                                      Afspraak bekijken
                                    </Button>
                                    {isDraggable && (
                                      <Button
                                        variant="ghost"
                                        className="w-full justify-start h-8 text-sm"
                                        onClick={() => {
                                          setEditAppointment(apt);
                                          setAppointmentMenuOpen(false);
                                        }}
                                      >
                                        <Edit className="w-4 h-4 mr-2" />
                                        Afspraak bewerken
                                      </Button>
                                    )}
                                    {isAdmin && !apt.id.startsWith('installation-') && (
                                      <Button
                                        variant="ghost"
                                        className="w-full justify-start h-8 text-sm text-destructive hover:text-destructive"
                                        onClick={() => {
                                          setAppointmentMenuOpen(false);
                                          handleDelete(apt.id);
                                        }}
                                        data-testid={`delete-appointment-${apt.id}`}
                                      >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Verwijderen
                                      </Button>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            );
                          })}
                          {dayAppointments.length > 3 && (
                            <div 
                              className="text-[10px] text-muted-foreground font-medium px-1 cursor-pointer hover:text-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDayDetailDate(dayStr);
                                setDayDetailOpen(true);
                                setDayMenuOpen(false);
                              }}
                            >
                              +{dayAppointments.length - 3} meer
                            </div>
                          )}
                        </div>
                      </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-2" align="start">
                          <div className="space-y-1">
                            <div className="text-sm font-semibold pb-2 border-b mb-2">
                              {format(day, 'EEEE d MMMM', { locale: nl })}
                            </div>
                            <Button
                              variant="ghost"
                              className="w-full justify-start h-9 text-sm"
                              onClick={() => {
                                setNewAppointmentDefaults({ date: dayStr });
                                setDialogOpen(true);
                                setDayMenuOpen(false);
                              }}
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Afspraak toevoegen
                            </Button>
                            <Button
                              variant="ghost"
                              className="w-full justify-start h-9 text-sm"
                              onClick={() => {
                                setDayDetailDate(dayStr);
                                setDayDetailOpen(true);
                                setDayMenuOpen(false);
                              }}
                            >
                              <CalendarIcon className="w-4 h-4 mr-2" />
                              Dagplanning bekijken
                              {dayAppointments.length > 0 && (
                                <Badge variant="secondary" className="ml-auto">
                                  {dayAppointments.length}
                                </Badge>
                              )}
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    );
                  });
                })()}
              </div>
            </div>
          ) : (
            /* Week View Grid */
            <div className="flex-1 overflow-auto" data-testid="calendar-weekly-grid">
            {/* Day headers */}
            <div className="grid grid-cols-[70px_repeat(7,1fr)] border-b sticky top-0 z-20 bg-card">
              <div className="p-2 border-r text-center text-xs text-muted-foreground">
                GMT+1
              </div>
              {Array.from({ length: 7 }, (_, i) => {
                const day = addDays(startOfWeek(selectedDate, { weekStartsOn: 1 }), i);
                const dayApptsCount = appointments.filter(a => 
                  isDayInAppointmentRange(format(day, 'yyyy-MM-dd'), a) && 
                  typeFilters[a.appointment_type] !== false
                ).length;
                
                return (
                  <div 
                    key={i} 
                    className={`p-2 border-r text-center cursor-pointer transition-colors ${
                      isToday(day) ? 'bg-primary/5' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => setSelectedDate(day)}
                  >
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">
                      {format(day, 'EEE', { locale: nl })}
                    </div>
                    <div className={`text-2xl font-medium mt-1 ${
                      isToday(day) 
                        ? 'bg-primary text-primary-foreground rounded-full w-10 h-10 flex items-center justify-center mx-auto' 
                        : isSameDay(day, selectedDate) 
                          ? 'text-primary' 
                          : ''
                    }`}>
                      {format(day, 'd')}
                    </div>
                    {dayApptsCount > 0 && (
                      <div className="text-[10px] text-muted-foreground mt-1">
                        {dayApptsCount} afspraak{dayApptsCount > 1 ? 'en' : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Time Grid */}
            <div className="grid grid-cols-[70px_repeat(7,1fr)] relative">
              {/* Time labels column */}
              <div className="border-r">
                {TIME_SLOTS.map((hour) => (
                  <div key={hour} className="h-16 text-xs text-muted-foreground pr-2 text-right relative -top-2">
                    {`${String(hour).padStart(2, '0')}:00`}
                  </div>
                ))}
              </div>
              
              {/* Day columns */}
              {Array.from({ length: 7 }, (_, i) => {
                const day = addDays(startOfWeek(selectedDate, { weekStartsOn: 1 }), i);
                const dayStr = format(day, 'yyyy-MM-dd');
                const dayAppointments = appointments
                  .filter(a => isDayInAppointmentRange(dayStr, a) && typeFilters[a.appointment_type] !== false);
                
                return (
                  <div 
                    key={i} 
                    className={`border-r relative ${isToday(day) ? 'bg-primary/5' : ''}`}
                  >
                    {/* Time slot rows */}
                    {TIME_SLOTS.map((hour) => {
                      const slotKey = `${dayStr}-${hour}`;
                      const isDragOverSlot = dragOverTarget === slotKey;
                      
                      return (
                        <div 
                          key={hour}
                          className={`h-16 border-b border-dashed border-muted group cursor-pointer transition-all ${
                            isDragOverSlot ? 'bg-primary/20' : 'hover:bg-muted/30'
                          }`}
                          onClick={() => {
                            setSelectedDate(day);
                            setNewAppointmentDefaults({ 
                              time: `${String(hour).padStart(2, '0')}:00`,
                              date: format(day, 'yyyy-MM-dd')
                            });
                            setDialogOpen(true);
                          }}
                          onDragOver={(e) => handleDragOver(e, dayStr, hour)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, dayStr, hour)}
                        >
                          <div className={`text-xs text-muted-foreground p-1 transition-opacity ${
                            isDragOverSlot ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                          }`}>
                            {isDragOverSlot ? 'Hier loslaten' : '+ Nieuwe afspraak'}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Appointment blocks */}
                    {dayAppointments.map((apt) => {
                      const spanPos = getSpanPosition(dayStr, apt);
                      const isMultiDay = spanPos !== 'single';
                      const isFirstDay = spanPos === 'start' || spanPos === 'single';
                      const [hours, minutes] = (apt.time || '09:00').split(':').map(Number);
                      const startMinutes = (hours - 8) * 60 + minutes;
                      const duration = apt.duration_minutes || 60;
                      // Multi-day: on non-start days, fill the full visible workday (8-19)
                      const top = isFirstDay ? Math.max(0, (startMinutes / 60) * 64) : 0;
                      const height = isFirstDay
                        ? Math.max((duration / 60) * 64, 28)
                        : 11 * 64; // Full workday height (8:00 to 19:00 = 11h)

                      const typeConfig = {
                        cleaning: { bg: 'bg-purple-500', text: 'text-white', light: 'bg-purple-100', icon: Sparkles },
                        sales: { bg: 'bg-blue-500', text: 'text-white', light: 'bg-blue-100', icon: Users },
                        installation: { bg: 'bg-amber-500', text: 'text-white', light: 'bg-amber-100', icon: Wrench },
                        electricity: { bg: 'bg-red-500', text: 'text-white', light: 'bg-red-100', icon: Zap },
                        intern: { bg: 'bg-emerald-600', text: 'text-white', light: 'bg-emerald-100', icon: Building2 },
                      }[apt.appointment_type] || { bg: 'bg-gray-500', text: 'text-white', light: 'bg-gray-100', icon: CalendarIcon };
                      
                      const Icon = typeConfig.icon;
                      const customer = customers.find(c => c.id === apt.customer_id);
                      const property = properties.find(p => p.id === apt.property_id);
                      // Only allow dragging from the start day of a multi-day range
                      const isDraggable = !apt.read_only && !apt.is_google_event && (!isMultiDay || isFirstDay);

                      // Span-aware rounded corners so the bar visually continues
                      let radiusClass = 'rounded-md';
                      if (spanPos === 'start') radiusClass = 'rounded-l-md rounded-r-none';
                      else if (spanPos === 'middle') radiusClass = 'rounded-none';
                      else if (spanPos === 'end') radiusClass = 'rounded-r-md rounded-l-none';

                      return (
                        <HoverCard key={`${apt.id}-${dayStr}`} openDelay={200}>
                          <HoverCardTrigger asChild>
                            <div
                              draggable={isDraggable}
                              onDragStart={(e) => handleDragStart(e, apt)}
                              onDragEnd={handleDragEnd}
                              className={`absolute left-1 right-1 ${typeConfig.bg} ${typeConfig.text} ${radiusClass} px-2 py-1 text-xs overflow-hidden shadow-sm hover:shadow-lg hover:scale-[1.02] z-10 transition-all ${isFirstDay ? 'border-l-4 border-l-white/30' : ''} ${
                                isDraggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                              } ${draggedAppointment?.id === apt.id ? 'opacity-50 scale-95' : ''}`}
                              style={{ top: `${top}px`, height: `${height}px` }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!draggedAppointment) setEditAppointment(apt);
                              }}
                              data-testid={`appointment-block-${apt.id}-${dayStr}`}
                            >
                              <div className="font-semibold truncate flex items-center gap-1">
                                <Icon className="w-3 h-3 shrink-0" />
                                <span className="truncate">{apt.title}</span>
                              </div>
                              {isFirstDay && height > 40 && apt.time && (
                                <div className="text-[10px] opacity-90">
                                  {apt.time} - {(() => {
                                    const [h, m] = apt.time.split(':').map(Number);
                                    const endMinutes = h * 60 + m + (apt.duration_minutes || 60);
                                    return `${String(Math.floor(endMinutes / 60)).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;
                                  })()}
                                </div>
                              )}
                              {isFirstDay && height > 55 && customer && (
                                <div className="text-[10px] opacity-80 truncate mt-0.5">
                                  {customer.first_name} {customer.last_name}
                                </div>
                              )}
                              {isMultiDay && !isFirstDay && (
                                <div className="text-[10px] opacity-80 mt-0.5">
                                  Meerdaags {spanPos === 'end' ? '(einde)' : '(loopt door)'}
                                </div>
                              )}
                            </div>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-80 p-0" side="right">
                            <div className={`${typeConfig.bg} ${typeConfig.text} p-3`}>
                              <div className="flex items-center gap-2">
                                <Icon className="w-5 h-5" />
                                <span className="font-semibold">{apt.title}</span>
                              </div>
                            </div>
                            <div className="p-3 space-y-2">
                              <div className="flex items-center gap-2 text-sm">
                                <Clock className="w-4 h-4 text-muted-foreground" />
                                <span>{format(new Date(apt.date), 'EEEE d MMMM', { locale: nl })}</span>
                                <span className="text-muted-foreground">•</span>
                                <span>{apt.time} - {(() => {
                                  const [h, m] = (apt.time || '09:00').split(':').map(Number);
                                  const endMinutes = h * 60 + m + (apt.duration_minutes || 60);
                                  return `${String(Math.floor(endMinutes / 60)).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;
                                })()}</span>
                              </div>
                              {customer && (
                                <div className="flex items-center gap-2 text-sm">
                                  <User className="w-4 h-4 text-muted-foreground" />
                                  <span>{customer.first_name} {customer.last_name}</span>
                                </div>
                              )}
                              {property && (
                                <div className="flex items-center gap-2 text-sm">
                                  <MapPin className="w-4 h-4 text-muted-foreground" />
                                  <span className="truncate">{property.street} {property.number}, {property.city}</span>
                                </div>
                              )}
                              {apt.notes && (
                                <div className="text-sm text-muted-foreground border-t pt-2 mt-2">
                                  {apt.notes}
                                </div>
                              )}
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Upcoming appointments */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Aankomende Afspraken</CardTitle>
        </CardHeader>
        <CardContent>
          {appointments
            .filter(a => a.date >= formatLocalDate(new Date()) && a.status === 'scheduled')
            .sort((a, b) => a.date.localeCompare(b.date) || (a.time || '').localeCompare(b.time || ''))
            .slice(0, 5)
            .map(apt => {
              const type = APPOINTMENT_TYPES[apt.appointment_type];
              const Icon = type?.icon || Wrench;
              const isEditable = !apt.read_only && !apt.is_google_event;
              const isCleaning = apt.id.startsWith('cleaning-');
              const isInstallation = apt.id.startsWith('installation-') || apt.id.startsWith('scheduled-');
              
              // Get property address for display
              const property = properties.find(p => p.id === apt.property_id);
              const addressDisplay = property 
                ? `${property.street} ${property.number}, ${property.city}`
                : (apt.property_street && apt.property_city ? `${apt.property_street}, ${apt.property_city}` : '');
              
              const handleClick = () => {
                if (!isEditable) return;
                if (isCleaning) {
                  // Open cleaning edit dialog
                  const originalId = apt.original_id || apt.id.replace('cleaning-', '');
                  setCleaningToEditData({ ...apt, cleaningId: originalId });
                  setEditCleaningData({
                    cleaning_date: apt.date,
                    cleaning_time: apt.time || '09:00',
                    cleaning_end_time: apt.end_time || '17:00',
                    notes: apt.notes || '',
                    send_confirmation: false
                  });
                  setEditCleaningOpen(true);
                } else if (isInstallation) {
                  // Open installation edit dialog
                  setInstallationToEditData(apt);
                  setEditInstallationData({
                    installation_date: apt.date,
                    installation_end_date: apt.end_date || apt.date,
                    installation_time: apt.time || '09:00',
                    installation_end_time: apt.end_time || '17:00',
                    notes: apt.notes || '',
                    send_confirmation: false
                  });
                  setEditInstallationOpen(true);
                } else {
                  setEditAppointment(apt);
                }
              };
              
              return (
                <div 
                  key={apt.id} 
                  className={`flex items-center justify-between py-3 border-b border-border/50 last:border-0 ${isEditable ? 'cursor-pointer hover:bg-secondary/50 rounded-md px-2 -mx-2 transition-colors' : ''}`}
                  onClick={handleClick}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                      type?.color === 'purple' ? 'bg-purple-500/20' :
                      type?.color === 'red' ? 'bg-red-500/20' :
                      type?.color === 'blue' ? 'bg-blue-500/20' : 'bg-amber-500/20'
                    }`}>
                      <Icon className={`w-4 h-4 ${
                        type?.color === 'purple' ? 'text-purple-400' :
                        type?.color === 'red' ? 'text-red-400' :
                        type?.color === 'blue' ? 'text-blue-400' : 'text-amber-400'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{apt.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(apt.date)} {apt.time && `om ${apt.time}`}
                        {addressDisplay && <span className="ml-2">• {addressDisplay}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isEditable && (
                      <Edit className="w-4 h-4 text-muted-foreground" />
                    )}
                    <Badge variant="outline" className={
                      type?.color === 'purple' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                      type?.color === 'red' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                      type?.color === 'blue' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 
                      'bg-amber-500/20 text-amber-400 border-amber-500/30'
                    }>
                      {type?.label}
                    </Badge>
                  </div>
                </div>
              );
            })}
          {appointments.filter(a => a.date >= formatLocalDate(new Date()) && a.status === 'scheduled').length === 0 && (
            <p className="text-center text-muted-foreground py-6">Geen aankomende afspraken</p>
          )}
        </CardContent>
      </Card>

      {/* Appointment Detail Dialog - Shows full details of a single appointment */}
      <Dialog open={appointmentDetailOpen} onOpenChange={setAppointmentDetailOpen}>
        <DialogContent className="w-screen h-[100dvh] sm:h-auto sm:w-full sm:max-w-lg sm:max-h-[85vh] sm:rounded-lg rounded-none p-4 sm:p-6 overflow-hidden flex flex-col">
          {appointmentDetailData && (() => {
            const apt = appointmentDetailData;
            const customer = customers.find(c => c.id === apt.customer_id);
            const property = properties.find(p => p.id === apt.property_id);
            const typeConfig = {
              cleaning: { bg: 'bg-purple-500', text: 'text-white', label: 'Reiniging', icon: Sparkles },
              sales: { bg: 'bg-blue-500', text: 'text-white', label: 'Sales', icon: Users },
              installation: { bg: 'bg-amber-500', text: 'text-white', label: 'Installatie', icon: Wrench },
              electricity: { bg: 'bg-red-500', text: 'text-white', label: 'Elektriciteit', icon: Zap },
              intern: { bg: 'bg-emerald-600', text: 'text-white', label: 'Intern', icon: Building2 },
            }[apt.appointment_type] || { bg: 'bg-gray-500', text: 'text-white', label: 'Afspraak', icon: CalendarIcon };
            const Icon = typeConfig.icon;
            const isDraggable = !apt.read_only && !apt.is_google_event;
            
            return (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-3">
                    <div className={`${typeConfig.bg} ${typeConfig.text} p-3 rounded-lg`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <DialogTitle className="text-xl">{apt.title}</DialogTitle>
                      <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                        <Badge variant="secondary">{typeConfig.label}</Badge>
                        <Badge variant={apt.status === 'completed' ? 'default' : apt.status === 'cancelled' ? 'destructive' : 'outline'}>
                          {apt.status === 'scheduled' ? 'Gepland' : apt.status === 'completed' ? 'Voltooid' : apt.status === 'cancelled' ? 'Geannuleerd' : apt.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </DialogHeader>
                
                <div className="flex-1 overflow-auto space-y-4 py-4">
                  {/* Date & Time */}
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4" />
                      Datum & Tijd
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-muted-foreground">Datum:</span>
                        <p className="font-medium">{apt.date ? format(parse(apt.date, 'yyyy-MM-dd', new Date()), 'EEEE d MMMM yyyy', { locale: nl }) : '-'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Tijd:</span>
                        <p className="font-medium">
                          {apt.all_day ? 'Hele dag' : (apt.time || '-')}
                          {!apt.all_day && apt.end_time && ` - ${apt.end_time}`}
                          {!apt.all_day && !apt.end_time && apt.duration_minutes && ` (${apt.duration_minutes} min)`}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Customer Info */}
                  {customer && (
                    <div className="bg-muted/50 rounded-lg p-4">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Klantgegevens
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{customer.first_name} {customer.last_name}</span>
                        </div>
                        {customer.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            <a href={`mailto:${customer.email}`} className="text-primary hover:underline">{customer.email}</a>
                          </div>
                        )}
                        {customer.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <a href={`tel:${customer.phone}`} className="text-primary hover:underline">{customer.phone}</a>
                          </div>
                        )}
                        {customer.mobile && customer.mobile !== customer.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <a href={`tel:${customer.mobile}`} className="text-primary hover:underline">{customer.mobile} (mobiel)</a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Property/Address Info */}
                  {property && (
                    <div className="bg-muted/50 rounded-lg p-4">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Adres
                      </h4>
                      <div className="text-sm space-y-1">
                        <p className="font-medium">{property.street} {property.number}</p>
                        <p>{property.postal_code} {property.city}</p>
                        {property.extra_info && (
                          <p className="text-muted-foreground text-xs mt-2">{property.extra_info}</p>
                        )}
                      </div>
                      {property.street && property.city && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3"
                          onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${property.street} ${property.number}, ${property.postal_code} ${property.city}`)}`, '_blank')}
                        >
                          <MapPin className="w-3 h-3 mr-1" />
                          Open in Google Maps
                        </Button>
                      )}
                    </div>
                  )}
                  
                  {/* Panel count - Only for cleaning appointments */}
                  {apt.appointment_type === 'cleaning' && (() => {
                    const panelCount = apt.panel_count || getPanelCountForProperty(apt.property_id);
                    return panelCount > 0 ? (
                      <div className="bg-purple-50 dark:bg-purple-950/30 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                        <h4 className="font-semibold text-sm mb-3 flex items-center gap-2 text-purple-700 dark:text-purple-300">
                          <Sparkles className="w-4 h-4" />
                          Installatie Details
                        </h4>
                        <div className="flex items-center gap-3">
                          <div className="bg-purple-500 text-white p-2 rounded-lg">
                            <span className="text-2xl font-bold">{panelCount}</span>
                          </div>
                          <div>
                            <p className="font-medium text-purple-700 dark:text-purple-300">Zonnepanelen</p>
                            <p className="text-sm text-muted-foreground">te reinigen op dit adres</p>
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })()}
                  
                  {/* Assigned to / Created by */}
                  <div className="bg-muted/50 rounded-lg p-4">
                    <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Toewijzing
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {apt.created_by_name && (
                        <div>
                          <span className="text-muted-foreground">Aanmaker:</span>
                          <p className="font-medium" data-testid="appointment-creator">{apt.created_by_name}</p>
                        </div>
                      )}
                      {apt.assigned_to_name && (
                        <div>
                          <span className="text-muted-foreground">Uitvoerder:</span>
                          <p className="font-medium">{apt.assigned_to_name}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Notes */}
                  {/* Foto's (legplan, wateraansluiting, ...) */}
                  {(() => {
                    const photoTarget = getPhotoTarget(apt);
                    const photos = apt.photos || [];
                    const canManagePhotos = isAdmin && photoTarget;
                    if (!canManagePhotos && photos.length === 0) return null;
                    return (
                      <div className="bg-muted/50 rounded-lg p-4" data-testid="appointment-photos-section">
                        <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                          <Camera className="w-4 h-4" />
                          Foto's & documenten ({photos.length})
                        </h4>
                        {photos.length > 0 && (
                          <div className="grid grid-cols-3 gap-2 mb-3">
                            {photos.map(p => (
                              <div key={p.id} className="relative group">
                                <button
                                  type="button"
                                  onClick={() => setLightboxDoc({ url: p.url, filename: p.filename || 'Foto' })}
                                  className="w-full focus:outline-none"
                                  data-testid={`open-photo-${p.id}`}
                                >
                                  {/\.pdf($|\?)/i.test(p.url || '') || /\.pdf$/i.test(p.filename || '') ? (
                                    <div className="w-full h-24 rounded-md border bg-muted/40 flex flex-col items-center justify-center gap-1 hover:bg-muted transition-colors">
                                      <FileText className="w-7 h-7 text-red-500" />
                                      <span className="text-[10px] text-muted-foreground truncate max-w-full px-1">{p.filename || 'PDF'}</span>
                                    </div>
                                  ) : (
                                    <img
                                      src={p.url}
                                      alt={p.description || p.filename || 'Foto'}
                                      className="w-full h-24 object-cover rounded-md border hover:opacity-90 transition-opacity"
                                    />
                                  )}
                                </button>
                                {canManagePhotos && (
                                  <button
                                    type="button"
                                    onClick={() => handleAppointmentPhotoDelete(apt, p.id)}
                                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    data-testid={`delete-photo-${p.id}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {canManagePhotos && (
                          <label className="inline-flex">
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              className="hidden"
                              disabled={uploadingPhoto}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleAppointmentPhotoUpload(apt, f);
                                e.target.value = '';
                              }}
                              data-testid="appointment-photo-input"
                            />
                            <span className={`inline-flex items-center gap-2 text-sm border rounded-md px-3 py-1.5 cursor-pointer hover:bg-muted transition-colors ${uploadingPhoto ? 'opacity-50 pointer-events-none' : ''}`}>
                              {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                              {uploadingPhoto ? 'Uploaden...' : 'Foto / document toevoegen'}
                            </span>
                          </label>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* Plannen & bijlagen uit de installatie-offerte (automatisch zichtbaar) */}
                  {offerteDocsForDetail.length > 0 && (
                    <div className="bg-muted/50 rounded-lg p-4" data-testid="offerte-plans-section">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Plannen uit offerte ({offerteDocsForDetail.length})
                      </h4>
                      <div className="grid grid-cols-3 gap-2">
                        {offerteDocsForDetail.map(doc => {
                          const docUrl = doc.r2_public_url || `${API_URL}/api/documents/${doc.id}/file`;
                          const isPdf = /\.pdf$/i.test(doc.filename || '');
                          return (
                            <button
                              key={doc.id}
                              type="button"
                              onClick={() => setLightboxDoc({ url: docUrl, filename: doc.filename || 'Document' })}
                              className="w-full focus:outline-none"
                              data-testid={`open-offerte-plan-${doc.id}`}
                            >
                              {isPdf ? (
                                <div className="w-full h-24 rounded-md border bg-muted/40 flex flex-col items-center justify-center gap-1 hover:bg-muted transition-colors">
                                  <FileText className="w-7 h-7 text-red-500" />
                                  <span className="text-[10px] text-muted-foreground truncate max-w-full px-1">{doc.filename || 'PDF'}</span>
                                </div>
                              ) : (
                                <img
                                  src={docUrl}
                                  alt={doc.filename || 'Plan'}
                                  className="w-full h-24 object-cover rounded-md border hover:opacity-90 transition-opacity"
                                  loading="lazy"
                                />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {(apt.notes || apt.description) && (
                    <div className="bg-muted/50 rounded-lg p-4">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Notities
                      </h4>
                      <p className="text-sm whitespace-pre-wrap">{apt.notes || apt.description}</p>
                    </div>
                  )}
                </div>
                
                <DialogFooter className="border-t pt-4 gap-2 flex-col sm:flex-row">
                  {isAdmin && !apt.id.startsWith('installation-') && (
                    <Button
                      variant="destructive"
                      className="mr-auto"
                      onClick={() => {
                        setAppointmentDetailOpen(false);
                        handleDelete(apt.id);
                      }}
                      data-testid="detail-delete-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Verwijderen
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setAppointmentDetailOpen(false)}
                  >
                    Sluiten
                  </Button>
                  {isDraggable && (
                    <Button
                      onClick={() => {
                        setEditAppointment(apt);
                        setAppointmentDetailOpen(false);
                      }}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Bewerken
                    </Button>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Day Detail Dialog - Shows all appointments for a specific day */}
      <Dialog open={dayDetailOpen} onOpenChange={setDayDetailOpen}>
        <DialogContent className="w-screen h-[100dvh] sm:h-auto sm:w-full sm:max-w-lg sm:max-h-[85vh] sm:rounded-lg rounded-none p-4 sm:p-6 overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" />
              Dagplanning
            </DialogTitle>
            <DialogDescription>
              {dayDetailDate && format(parse(dayDetailDate, 'yyyy-MM-dd', new Date()), 'EEEE d MMMM yyyy', { locale: nl })}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto space-y-2 py-2">
            {dayDetailDate && (() => {
              const dayAppts = appointments
                .filter(a => a.date === dayDetailDate && typeFilters[a.appointment_type] !== false)
                .sort((a, b) => (a.time || '').localeCompare(b.time || ''));
              
              if (dayAppts.length === 0) {
                return (
                  <div className="text-center py-8">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">Geen afspraken gepland</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => {
                        setNewAppointmentDefaults({ date: dayDetailDate });
                        setDialogOpen(true);
                        setDayDetailOpen(false);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Afspraak toevoegen
                    </Button>
                  </div>
                );
              }
              
              return dayAppts.map((apt) => {
                const typeConfig = {
                  cleaning: { bg: 'bg-purple-500', text: 'text-white', label: 'Reiniging', icon: Sparkles },
                  sales: { bg: 'bg-blue-500', text: 'text-white', label: 'Sales', icon: Users },
                  installation: { bg: 'bg-amber-500', text: 'text-white', label: 'Installatie', icon: Wrench },
                  electricity: { bg: 'bg-red-500', text: 'text-white', label: 'Elektriciteit', icon: Zap },
                  intern: { bg: 'bg-emerald-600', text: 'text-white', label: 'Intern', icon: Building2 },
                }[apt.appointment_type] || { bg: 'bg-gray-500', text: 'text-white', label: 'Afspraak', icon: CalendarIcon };
                
                const Icon = typeConfig.icon;
                const customer = customers.find(c => c.id === apt.customer_id);
                const property = properties.find(p => p.id === apt.property_id);
                const isEditable = !apt.read_only && !apt.is_google_event;
                
                return (
                  <div 
                    key={apt.id}
                    className={`p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors ${
                      isEditable ? '' : 'opacity-75'
                    }`}
                    onClick={() => {
                      if (isEditable) {
                        setEditAppointment(apt);
                        setDayDetailOpen(false);
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`${typeConfig.bg} ${typeConfig.text} p-2 rounded-lg shrink-0`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold truncate">{apt.title}</span>
                          <Badge variant="secondary" className="shrink-0 text-[10px]">
                            {typeConfig.label}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          {apt.time && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <span>
                                {apt.time}
                                {apt.end_time && ` - ${apt.end_time}`}
                                {!apt.end_time && apt.duration_minutes && ` - ${(() => {
                                  const [h, m] = apt.time.split(':').map(Number);
                                  const endMinutes = h * 60 + m + apt.duration_minutes;
                                  return `${String(Math.floor(endMinutes / 60)).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;
                                })()}`}
                              </span>
                            </div>
                          )}
                          {customer && (
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              <span>{customer.first_name} {customer.last_name}</span>
                            </div>
                          )}
                          {property && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              <span className="truncate">{property.street} {property.number}, {property.city}</span>
                            </div>
                          )}
                        </div>
                        {apt.notes && (
                          <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{apt.notes}</p>
                        )}
                      </div>
                      {isEditable && (
                        <Edit className="w-4 h-4 text-muted-foreground shrink-0" />
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button
              variant="outline"
              onClick={() => setDayDetailOpen(false)}
            >
              Sluiten
            </Button>
            <Button
              onClick={() => {
                setNewAppointmentDefaults({ date: dayDetailDate });
                setDialogOpen(true);
                setDayDetailOpen(false);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Afspraak toevoegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) setNewAppointmentDefaults(null);
      }}>
        <DialogContent className="w-screen h-[100dvh] sm:h-auto sm:w-full sm:max-w-lg sm:max-h-[90vh] sm:rounded-lg rounded-none p-4 sm:p-6 overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nieuwe Afspraak</DialogTitle>
            <DialogDescription>Plan een nieuwe afspraak in</DialogDescription>
          </DialogHeader>
          <AppointmentForm
            appointment={newAppointmentDefaults}
            customers={customers}
            properties={properties}
            users={users}
            allAppointments={appointments}
            onSubmit={handleCreate}
            onCancel={() => {
              setDialogOpen(false);
              setNewAppointmentDefaults(null);
            }}
            loading={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editAppointment} onOpenChange={() => setEditAppointment(null)}>
        <DialogContent className="w-screen h-[100dvh] sm:h-auto sm:w-full sm:max-w-lg sm:max-h-[90vh] sm:rounded-lg rounded-none p-4 sm:p-6 overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Afspraak Bewerken</DialogTitle>
            <DialogDescription>Pas de afspraak aan</DialogDescription>
          </DialogHeader>
          {editAppointment && (
            <AppointmentForm
              appointment={editAppointment}
              customers={customers}
              properties={properties}
              users={users}
              allAppointments={appointments}
              existingPhotos={editAppointment.photos || []}
              onUploadPhoto={(file) => handleAppointmentPhotoUpload(editAppointment, file)}
              onDeletePhoto={(photoId) => handleAppointmentPhotoDelete(editAppointment, photoId)}
              onSubmit={handleUpdate}
              onCancel={() => setEditAppointment(null)}
              loading={saving}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Afspraak verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze afspraak wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setAppointmentToDelete(null)}>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Google Calendar Popup with Confirmation Email Option for Cleaning */}
      <Dialog open={calendarPopupOpen} onOpenChange={setCalendarPopupOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              {openGoogleCalendarAfter ? 'Reiniging inplannen' : 'Bevestigingsmail maken'}
            </DialogTitle>
            <DialogDescription>
              {openGoogleCalendarAfter 
                ? 'Wil je een bevestigingsmail sturen naar de klant?'
                : 'Maak een bevestigingsmail op basis van de "Afspraak reiniging" template'
              }
            </DialogDescription>
          </DialogHeader>
          
          {selectedCleaningForCalendar && selectedCleaningProperty && (
            <div className="space-y-4">
              {/* Appointment Summary */}
              <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">
                    {formatDate(selectedCleaningForCalendar.date)}
                    {selectedCleaningForCalendar.time && (
                      <span className="ml-2 text-muted-foreground">
                        om {selectedCleaningForCalendar.time}
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <span>
                    {selectedCleaningProperty.street} {selectedCleaningProperty.number}, {selectedCleaningProperty.postal_code} {selectedCleaningProperty.city}
                  </span>
                </div>
                {selectedCleaningCustomer && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>
                      {selectedCleaningCustomer.first_name} {selectedCleaningCustomer.last_name}
                    </span>
                  </div>
                )}
                {getPanelCountForProperty(selectedCleaningProperty.id) > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-muted-foreground" />
                    <span>{getPanelCountForProperty(selectedCleaningProperty.id)} zonnepanelen</span>
                  </div>
                )}
              </div>

              {/* Email Options - Radio buttons */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Bevestigingsmail</Label>
                <div className="space-y-2">
                  <div 
                    className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${emailAction === 'queue' ? 'bg-blue-500/10 border-blue-500' : 'hover:bg-muted/50'}`}
                    onClick={() => setEmailAction('queue')}
                  >
                    <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${emailAction === 'queue' ? 'border-blue-500' : 'border-muted-foreground'}`}>
                      {emailAction === 'queue' && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-500" />
                        Mail klaarzetten
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Mail komt in de wachtrij zodat je deze eerst kunt nakijken
                      </p>
                    </div>
                  </div>
                  
                  <div 
                    className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${emailAction === 'send' ? 'bg-green-500/10 border-green-500' : 'hover:bg-muted/50'}`}
                    onClick={() => setEmailAction('send')}
                  >
                    <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${emailAction === 'send' ? 'border-green-500' : 'border-muted-foreground'}`}>
                      {emailAction === 'send' && <div className="w-2 h-2 rounded-full bg-green-500" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium flex items-center gap-2">
                        <Send className="w-4 h-4 text-green-500" />
                        Mail direct verzenden
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Mail wordt meteen verstuurd naar de klant
                      </p>
                    </div>
                  </div>
                  
                  <div 
                    className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${emailAction === 'none' ? 'bg-muted/50 border-muted-foreground' : 'hover:bg-muted/50'}`}
                    onClick={() => setEmailAction('none')}
                  >
                    <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${emailAction === 'none' ? 'border-muted-foreground' : 'border-muted-foreground'}`}>
                      {emailAction === 'none' && <div className="w-2 h-2 rounded-full bg-muted-foreground" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-muted-foreground">
                        Geen mail versturen
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setCalendarPopupOpen(false);
                setSelectedCleaningForCalendar(null);
                setSelectedCleaningCustomer(null);
                setSelectedCleaningProperty(null);
                setEmailAction('queue');
              }}
            >
              Annuleren
            </Button>
            <Button 
              onClick={handleCalendarConfirm}
              disabled={sendingEmail}
              className="gap-2"
              variant={emailAction === 'send' ? 'default' : 'outline'}
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {emailAction === 'send' ? 'Verzenden...' : 'Verwerken...'}
                </>
              ) : (
                <>
                  <CalendarIcon className="w-4 h-4" />
                  Doorgaan
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Cleaning Dialog */}
      <Dialog open={editCleaningOpen} onOpenChange={setEditCleaningOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Reiniging Bewerken
            </DialogTitle>
            <DialogDescription>
              Pas de datum en tijd aan van deze reiniging
            </DialogDescription>
          </DialogHeader>
          
          {cleaningToEditData && (
            <div className="space-y-4">
              {/* Klant info */}
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="font-medium">
                  {cleaningToEditData.customer_name || (() => {
                    const prop = properties.find(p => p.id === cleaningToEditData.property_id);
                    const cust = prop ? customers.find(c => c.id === prop.customer_id) : null;
                    return cust ? `${cust.first_name} ${cust.last_name}` : 'Onbekende klant';
                  })()}
                </p>
                <p className="text-sm text-muted-foreground">
                  {cleaningToEditData.property?.street} {cleaningToEditData.property?.number}, {cleaningToEditData.property?.city}
                </p>
              </div>

              {/* Datum en tijd */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Datum *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {editCleaningData.cleaning_date 
                          ? format(parse(editCleaningData.cleaning_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: nl }) 
                          : 'Selecteer datum'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={editCleaningData.cleaning_date ? parse(editCleaningData.cleaning_date, 'yyyy-MM-dd', new Date()) : undefined}
                        onSelect={(date) => date && setEditCleaningData(prev => ({ ...prev, cleaning_date: format(date, 'yyyy-MM-dd') }))}
                        locale={nl}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Tijd</Label>
                  <Input
                    type="time"
                    value={editCleaningData.cleaning_time}
                    onChange={(e) => setEditCleaningData(prev => ({ ...prev, cleaning_time: e.target.value }))}
                  />
                </div>
              </div>

              {/* Notes/Opmerkingen veld */}
              <div className="space-y-2">
                <Label>Opmerkingen</Label>
                <Textarea
                  value={editCleaningData.notes}
                  onChange={(e) => setEditCleaningData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  placeholder="Extra opmerkingen voor de kuisman..."
                />
              </div>

              {/* Bevestigingsmail checkbox */}
              <div className="flex items-start space-x-3 p-4 rounded-lg border bg-blue-500/5 border-blue-500/20">
                <Checkbox
                  id="send-edit-cleaning-confirmation"
                  checked={editCleaningData.send_confirmation}
                  onCheckedChange={(checked) => setEditCleaningData(prev => ({ ...prev, send_confirmation: checked }))}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Label 
                    htmlFor="send-edit-cleaning-confirmation" 
                    className="text-sm font-medium cursor-pointer flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4 text-blue-500" />
                    Nieuwe bevestigingsmail verzenden
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Stuurt een email naar de klant met de gewijzigde afspraakdatum
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setEditCleaningOpen(false);
                setCleaningToEditData(null);
              }}
            >
              Annuleren
            </Button>
            <Button 
              onClick={handleEditCleaningSubmit}
              disabled={editingCleaning || !editCleaningData.cleaning_date}
              className="gap-2"
            >
              {editingCleaning ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Opslaan...
                </>
              ) : (
                <>
                  <Edit className="w-4 h-4" />
                  Wijzigingen Opslaan
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Installation Dialog */}
      <Dialog open={editInstallationOpen} onOpenChange={setEditInstallationOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Installatie Bewerken
            </DialogTitle>
            <DialogDescription>
              Pas de datum en tijd aan van deze installatie
            </DialogDescription>
          </DialogHeader>
          
          {installationToEditData && (
            <div className="space-y-4">
              {/* Klant info */}
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="font-medium">
                  {installationToEditData.customer_name || (() => {
                    const prop = properties.find(p => p.id === installationToEditData.property_id);
                    const cust = prop ? customers.find(c => c.id === prop.customer_id) : null;
                    return cust ? `${cust.first_name} ${cust.last_name}` : 'Onbekende klant';
                  })()}
                </p>
                <p className="text-sm text-muted-foreground">
                  {installationToEditData.property_street 
                    ? `${installationToEditData.property_street}, ${installationToEditData.property_city}`
                    : (() => {
                        const prop = properties.find(p => p.id === installationToEditData.property_id);
                        return prop ? `${prop.street} ${prop.number}, ${prop.city}` : '';
                      })()
                  }
                </p>
                <Badge variant="outline" className="mt-2">
                  {installationToEditData.installation_type || installationToEditData.appointment_type}
                </Badge>
              </div>

              {/* Datum en tijd */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Startdatum *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {editInstallationData.installation_date 
                          ? format(parse(editInstallationData.installation_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: nl }) 
                          : 'Selecteer datum'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={editInstallationData.installation_date ? parse(editInstallationData.installation_date, 'yyyy-MM-dd', new Date()) : undefined}
                        onSelect={(date) => date && setEditInstallationData(prev => ({ ...prev, installation_date: format(date, 'yyyy-MM-dd') }))}
                        locale={nl}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Einddatum</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {editInstallationData.installation_end_date 
                          ? format(parse(editInstallationData.installation_end_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: nl }) 
                          : 'Zelfde dag'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={editInstallationData.installation_end_date ? parse(editInstallationData.installation_end_date, 'yyyy-MM-dd', new Date()) : undefined}
                        onSelect={(date) => date && setEditInstallationData(prev => ({ ...prev, installation_end_date: format(date, 'yyyy-MM-dd') }))}
                        locale={nl}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Starttijd</Label>
                  <Input
                    type="time"
                    value={editInstallationData.installation_time}
                    onChange={(e) => setEditInstallationData(prev => ({ ...prev, installation_time: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Eindtijd</Label>
                  <Input
                    type="time"
                    value={editInstallationData.installation_end_time}
                    onChange={(e) => setEditInstallationData(prev => ({ ...prev, installation_end_time: e.target.value }))}
                  />
                </div>
              </div>

              {/* Notes/Opmerkingen veld */}
              <div className="space-y-2">
                <Label>Notities</Label>
                <Textarea
                  value={editInstallationData.notes}
                  onChange={(e) => setEditInstallationData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  placeholder="Extra notities voor de installateurs..."
                />
              </div>

              {/* Bevestigingsmail checkbox */}
              <div className="flex items-start space-x-3 p-4 rounded-lg border bg-blue-500/5 border-blue-500/20">
                <Checkbox
                  id="send-edit-installation-confirmation"
                  checked={editInstallationData.send_confirmation}
                  onCheckedChange={(checked) => setEditInstallationData(prev => ({ ...prev, send_confirmation: checked }))}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Label 
                    htmlFor="send-edit-installation-confirmation" 
                    className="text-sm font-medium cursor-pointer flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4 text-blue-500" />
                    Nieuwe bevestigingsmail verzenden
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Stuurt een email naar de klant met de gewijzigde installatiedatum
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setEditInstallationOpen(false);
                setInstallationToEditData(null);
              }}
            >
              Annuleren
            </Button>
            <Button 
              onClick={handleEditInstallationSubmit}
              disabled={editingInstallation || !editInstallationData.installation_date}
              className="gap-2"
            >
              {editingInstallation ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Opslaan...
                </>
              ) : (
                <>
                  <Edit className="w-4 h-4" />
                  Wijzigingen Opslaan
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Popup viewer voor foto's & documenten */}
      <DocumentLightbox
        open={!!lightboxDoc}
        onOpenChange={(o) => { if (!o) setLightboxDoc(null); }}
        document={lightboxDoc}
      />
    </div>
  );
};

export default Agenda;
